<template>
  <div>
    <h4 class="table-page-header">
      Shifts
      <el-tooltip v-if="canCreateShift" content="Add New Shift">
        <el-button type="text" @click="createShift">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>

    <div class="compact-filter-section">
      <!-- Time Period & Date Controls Row -->
      <div class="filter-row-compact navigation-row">
        <div class="filter-group-compact view-group">
          <AIVSelect
            v-model="timePeriod"
            :options="periodOptions"
            placeholder="Period"
            @input="handlePeriodChange"
            class="compact-select"
          />
        </div>

        <!-- Custom Date Range Picker -->
        <div v-if="timePeriod === 'custom'" class="filter-group-compact date-group custom-date-range-group">
          <el-date-picker
            v-model="customDateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="compact-date-picker"
          />
        </div>

        <!-- Period-based Date Picker with Navigation -->
        <template v-else>
          <div class="filter-group-compact date-group date-nav-group">
            <button @click="goToPrevious" class="compact-nav-btn">
              <i class="fas fa-chevron-left"></i>
            </button>
            <el-date-picker
              ref="datePicker"
              v-model="selectedDate"
              :type="datePickerType"
              :format="dateFormat"
              :placeholder="placeholderText"
              class="compact-date-picker"
              :value-format="'yyyy-MM-dd'"
              popper-class="modern-date-picker-popper"
            >
              <template #prefix>
                <i class="el-input__icon el-icon-date"></i>
              </template>
            </el-date-picker>
            <button @click="goToNext" class="compact-nav-btn">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          <div class="filter-group-compact today-group">
            <button @click="goToToday" class="compact-today-btn">
              <i class="fas fa-bullseye"></i>
              Today
            </button>
          </div>
        </template>
      </div>

      <!-- Data Filters Row -->
      <div class="filter-row-compact data-row">
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedFilter"
            :options="filters"
            placeholder="Status"
            @input="handleStatusChange"
            class="compact-select"
          />
        </div>
        <div v-if="showFilters" class="filter-group-compact">
          <AIVSelect
            v-model="selectedCaregiver"
            :options="caregiverFilterOptions"
            placeholder="Caregiver"
            @input="handleCaregiverChange"
            class="compact-select"
          />
        </div>
        <div v-if="showFilters" class="filter-group-compact">
          <AIVSelect
            v-model="selectedClient"
            :options="clientFilterOptions"
            placeholder="Client"
            @input="handleClientChange"
            class="compact-select"
          />
        </div>
        <div v-if="userRole !== 'Caregiver'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedAgency"
            :options="agencyFilterOptions"
            placeholder="Agency"
            @input="handleAgencyChange"
            class="compact-select"
          />
        </div>
        <div v-if="userRole === 'Admin'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedAdmin"
            :options="adminFilterOptions"
            placeholder="Admin"
            @input="handleAdminChange"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedShiftType"
            :options="shiftTypeFilterOptions"
            placeholder="Type"
            @input="handleShiftTypeChange"
            class="compact-select"
          />
        </div>
        <div v-if="userRole !== 'Caregiver'" class="filter-group-compact">
          <AIVSelect
            v-model="selectedPayer"
            :options="payerFilterOptions"
            placeholder="Payer"
            @input="handlePayerChange"
            class="compact-select"
          />
        </div>
      </div>
    </div>

    <AIVTable
      :columns="shiftColumns"
      :data="shifts"
      :loading="loading"
      :pagination="pagination"
      :item-name="'shifts'"
      @pageChange="loadShifts"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
    >
      <template #cell-date="{ row }">
        {{ formatTableDate(row.date) }}
      </template>
      <template #cell-time="{ row }">
        <div class="time-display" :title="getTimeTooltip(row)">
          <div
            v-if="row.clock_in || row.clock_out || row.clockIn || row.clockOut"
            class="actual-time-primary"
          >
            {{ formatActualTimeRange(row) }}
          </div>
          <div v-else class="scheduled-time-primary">
            {{
              formatTimeRange(
                row.scheduled_start || row.startTime,
                row.scheduled_end || row.endTime,
                row.date,
              )
            }}
          </div>
          <div
            v-if="row.clock_in || row.clock_out || row.clockIn || row.clockOut"
            class="scheduled-time-secondary"
          >
            <small class="text-muted">
              Scheduled:
              {{
                formatTimeRange(
                  row.scheduled_start || row.startTime,
                  row.scheduled_end || row.endTime,
                  row.date,
                )
              }}
            </small>
          </div>
          <div
            v-for="sideRow in sideClockRows(row)"
            :key="`side-${sideRow.id || sideRow.id_users}`"
            class="side-clock-line"
          >
            <small>{{ sideRowLabel(row, sideRow) }}</small>
          </div>
          <div
            v-if="row.clock_in_geocode || row.clock_out_geocode"
            class="clock-location-indicator"
          >
            <a
              v-if="row.clock_in_geocode"
              :href="clockMapUrl(row.clock_in_geocode)"
              target="_blank"
              rel="noopener"
              class="clock-loc-link"
              title="Clock-in location — open in Google Maps"
              @click.stop
            ><i class="fa-solid fa-location-dot"></i> In</a>
            <a
              v-if="row.clock_out_geocode"
              :href="clockMapUrl(row.clock_out_geocode)"
              target="_blank"
              rel="noopener"
              class="clock-loc-link"
              title="Clock-out location — open in Google Maps"
              @click.stop
            ><i class="fa-solid fa-location-dot"></i> Out</a>
          </div>
          <div
            v-else-if="rowHasCaregiverClockLocations(row)"
            class="clock-location-indicator"
          >
            <span
              class="clock-loc-note"
              title="Per-caregiver clock locations recorded — open the shift to view"
            ><i class="fa-solid fa-location-dot"></i> Locations</span>
          </div>
        </div>
      </template>
      <template #cell-client="{ row }">
        <el-tooltip v-if="hasMultipleClients(row)" placement="bottom" effect="light" :open-delay="200">
          <div slot="content" v-html="getClientTooltipHtml(row)"></div>
          <span class="multiple-indicator">{{ row.clients.length }} clients</span>
        </el-tooltip>
        <span v-else>
          <router-link
            v-if="row.payer !== 'Non Service' && row.clients && row.clients[0] && row.clients[0].id"
            :to="`/pages/clients/${row.clients[0].id}`"
          >{{ formatClient(row) }}</router-link>
          <template v-else>{{ formatClient(row) }}</template>
        </span>
      </template>
      <template #cell-caregiver="{ row }">
        <el-tooltip v-if="hasMultipleCaregivers(row)" placement="bottom" effect="light" :open-delay="200">
          <div slot="content" v-html="getCaregiverTooltipHtml(row)"></div>
          <span class="multiple-indicator">{{ formatMultipleCaregivers(row) }}</span>
        </el-tooltip>
        <span v-else>
          <router-link
            v-if="row.caregivers && row.caregivers[0] && row.caregivers[0].id"
            :to="`/pages/caregivers/${row.caregivers[0].id}`"
          >{{ formatCaregiver(row) }}</router-link>
          <template v-else>{{ formatCaregiver(row) }}</template>
        </span>
      </template>
      <template #cell-service_code="{ row }">
        {{ formatServiceCode(row) }}
      </template>
      <template #cell-shift_status="{ row }">
        <div class="shift-status-container">
          <span :class="['status-badge', getStatusClass(getShiftStatus(row))]">
            {{ formatStatus(getShiftStatus(row)) }}
          </span>
          <span
            v-if="hasOrphanedSideHours(row)"
            class="offline-flag shadow-hours-flag"
            title="The primary caregiver never clocked in, but a shadow/training caregiver recorded hours on this shift (shown in the Time column). Their time is kept in their own clock record and included in payroll."
          >
            <i class="fa-solid fa-user-clock"></i> SHADOW WORKED
          </span>
          <span
            v-if="row._pendingSync"
            class="offline-flag pending-sync-flag"
            title="This clock event was recorded offline on this device and has not been uploaded yet. It uploads automatically when a connection is available."
          >
            <i class="fa-solid fa-cloud-arrow-up"></i> PENDING SYNC
          </span>
          <span
            v-else-if="row.clock_in_offline || row.clock_out_offline"
            class="offline-flag offline-recorded-flag"
            :title="offlineRecordedTitle(row)"
          >
            <i class="fa-solid fa-link-slash"></i> OFFLINE
          </span>
          <span
            v-if="isVRProximityFlagged(row)"
            class="proximity-flag"
            title="VR Shift - Proximity: Caregiver clocked in from outside 1 mile radius"
          >
            ⚠️ PROXIMITY
          </span>
        </div>
      </template>
      <template #cell-shift_notes="{ row }">
        <div class="notes-cell" :title="getShiftNotes(row)">
          {{ getShiftNotes(row) }}
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button
          v-if="canClockIn(row)"
          class="btn btn-sm clock-in-btn mr-2"
          @click="handleClockIn(row)"
          title="Clock In"
        >
          <i class="fa-solid fa-clock"></i> In
        </button>
        <button
          v-if="showTasksButton(row)"
          class="btn btn-sm btn-primary mr-2"
          @click="openGroupTasks(row)"
          title="Tasks"
        >
          <i class="fa-solid fa-list-check"></i> Tasks
        </button>
        <button
          v-if="canClockOut(row)"
          class="btn btn-sm clock-out-btn mr-2"
          @click="handleClockOut(row)"
          title="Clock Out"
        >
          <i class="fa-solid fa-clock"></i> Out
        </button>
        <button
          v-if="canAdminClockOut(row)"
          class="btn btn-sm clock-out-btn mr-2"
          @click="handleAdminClockOut(row)"
          title="Clock out on behalf of caregiver"
        >
          <i class="fa-solid fa-user-clock"></i> Admin Out
        </button>
        <button
          v-if="canStartBreak(row)"
          class="btn btn-sm start-break-btn mr-2"
          @click="handleStartBreak(row)"
          :title="row.isOnBreak ? 'End Break' : 'Start Break'"
        >
          <i
            :class="row.isOnBreak ? 'fa-solid fa-play' : 'fa-solid fa-pause'"
          ></i>
          {{ row.isOnBreak ? "End" : "Break" }}
        </button>
        <button
          v-if="canApproveShift(row)"
          class="btn btn-sm btn-success mr-2"
          @click="handleApproveShift(row)"
          title="Approve Shift"
        >
          <i class="fa-solid fa-check-circle"></i> Approve
        </button>
        <button
          v-if="canUndoApproval(row)"
          class="btn btn-sm btn-danger mr-2"
          @click="handleUndoApproval(row)"
          title="Undo Approval"
        >
          <i class="fa-solid fa-times-circle"></i> Undo Approval
        </button>
        <button
          v-if="canApproveMissedShift(row)"
          class="btn btn-sm btn-success mr-2"
          @click="handleApproveMissedShift(row)"
          title="Approve Missed Shift"
        >
          <i class="fa-solid fa-check-circle"></i> Approve
        </button>
        <button
          v-if="canChangeServiceCode(row)"
          class="btn btn-sm btn-primary mr-2"
          @click="openChangeServiceCodeModal(row)"
          title="Change Service Code"
        >
          <i class="fa-solid fa-code"></i>
        </button>
        <button
          v-if="canRecheckAuth(row)"
          class="btn btn-sm btn-warning mr-2"
          @click="handleRecheckAuth(row)"
          title="Re-check authorization hours/units"
        >
          <i class="fa-solid fa-rotate"></i> Check Auth
        </button>
        <button
          v-if="canEditShift(row)"
          class="btn btn-sm btn-warning mr-2"
          @click="editShift(row)"
          title="Edit"
        >
          <i class="fa-solid fa-pen-to-square"></i>
        </button>
        <button
          v-if="userRole === 'Admin' || userRole === 'Lead'"
          class="btn btn-sm btn-info mr-2"
          @click="viewShiftHistory(row)"
          title="View History"
        >
          <i class="fa-solid fa-history"></i>
        </button>
        <button
          v-if="canCancelShift(row)"
          class="btn btn-sm btn-danger mr-2"
          @click="cancelShift(row)"
          title="Cancel Shift"
        >
          <i class="fa-solid fa-ban"></i>
        </button>
        <button
          v-if="canDeleteShift(row)"
          class="btn btn-sm btn-danger"
          @click="deleteShift(row)"
          title="Delete"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </template>
    </AIVTable>

    <ShiftModal
      :visible.sync="showShiftModal"
      :shift-being-edited="shiftBeingEdited"
      :available-clients="availableClients"
      :available-caregivers="availableCaregivers"
      :saving="savingShift"
      @cancel="closeShiftModal"
      @save="saveShift"
    />

    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-in-success="onClockInSuccess"
    />

    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="currentShift"
      :user-id="userId"
      @clock-out-success="onClockOutSuccess"
    />

    <AdminClockOutModal
      :visible.sync="showAdminClockOutModal"
      :shift="shiftToAdminClockOut"
      @clocked-out="loadShifts"
    />

    <StartBreakModal
      :visible.sync="showStartBreakModal"
      :shift="currentShift"
      :user-id="userId"
      @break-action-success="onBreakActionSuccess"
    />

    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="groupTasksShift"
      :user-id="userId"
      @refresh="loadShifts"
    />

    <EditClockInTimeModal
      :visible.sync="showEditClockInTimeModal"
      :shift="shiftToEditClockInTime"
      @clock-in-updated="loadShifts"
    />

    <EditClockOutTimeModal
      :visible.sync="showEditClockOutTimeModal"
      :shift="shiftToEditClockOutTime"
      @clock-times-updated="loadShifts"
    />

    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="loadShifts"
    />

    <ApproveShiftModal
      :visible.sync="showApproveShiftModal"
      :shift="shiftToApprove"
      @shift-approved="loadShifts"
    />
    <ApproveMissedShift
      :visible.sync="showApproveMissedShiftModal"
      :shift="shiftToApproveMissed"
      @shift-approved="loadShifts"
    />
    <ChangeServiceCodeModal
      :visible.sync="showChangeServiceCodeModal"
      :shift="shiftToChangeServiceCode"
      @service-code-changed="loadShifts"
    />
    <ShiftHistoryModal
      :visible.sync="showShiftHistoryModal"
      :shift="shiftForHistory"
      :history="shiftHistory"
      :loading="loadingHistory"
      :instance-date="shiftInstanceDate"
    />

    <!-- Delete Recurring Shift Dialog -->
    <DeleteRecurringShiftDialog
      :visible.sync="deleteRecurringDialog.visible"
      @this-only="deleteThisShiftOnly"
      @this-and-future="deleteThisAndFutureShifts"
      @all-shifts="deleteAllShifts"
    />
  </div>
</template>

<script>
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { deletedPotentialShiftsService } from "@/serviceHandlers/deletedPotentialShiftsService";
import { officesService } from "@/serviceHandlers/officesService";
import { authorizationsService } from "@/serviceHandlers/authorizationsService";
import { geolocationService } from "@/serviceHandlers/geolocationService";
import AIVSelect from "@/components/AIVSelect.vue";
import ShiftModal from "@/components/ShiftModal.vue";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import AdminClockOutModal from "@/components/AdminClockOutModal.vue";
import StartBreakModal from "@/components/StartBreakModal.vue";
import EditClockInTimeModal from "@/components/EditClockInTimeModal.vue";
import EditClockOutTimeModal from "@/components/EditClockOutTimeModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";
import ApproveShiftModal from "@/components/ApproveShiftModal.vue";
import ApproveMissedShift from "@/components/ApproveMissedShift.vue";
import ChangeServiceCodeModal from "@/components/ChangeServiceCodeModal.vue";
import ShiftHistoryModal from "@/components/ShiftHistoryModal.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import DeleteRecurringShiftDialog from "@/components/DeleteRecurringShiftDialog.vue";
import { useLoginStore } from "@/stores/loginStore";
import { EventBus, EVENTS } from "@/utils/eventBus";
import { getCaregiverShiftStatus, canCaregiverClockIn, canCaregiverClockOut, isDSPDGroupShift, areAllGroupTasksComplete, getStatusIcon, formatCaregiverStatusHtml, caregiverNameWithRole, primaryCaregiverOf } from "@/utils/shiftStatus";
import { applyPendingClockEvents } from "@/utils/pendingClockState";
import { enqueue, generateEventId, onChange as onQueueChange } from "@/utils/offlineQueue";
import conflictMessageMixin from "@/mixins/conflictMessageMixin";
import clockLocationMixin from "@/mixins/clockLocationMixin";

export default {
  name: "Shifts",
  mixins: [conflictMessageMixin, clockLocationMixin],
  components: {
    AIVSelect,
    ShiftModal,
    ClockInModal,
    ClockOutModal,
    AdminClockOutModal,
    StartBreakModal,
    EditClockInTimeModal,
    EditClockOutTimeModal,
    EditShiftNotesModal,
    ApproveShiftModal,
    ApproveMissedShift,
    ChangeServiceCodeModal,
    ShiftHistoryModal,
    GroupShiftTasksModal,
    DeleteRecurringShiftDialog,
  },
  created() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    this.selectedDate = `${year}-${month}-${day}`;

    // Initialize user data early to avoid prop validation warnings
    const loginStore = useLoginStore();
    this.userRole = loginStore.user?.user_type || "Admin";
    this.userId = loginStore.user?.id || "1";
  },
  data() {
    return {
      shifts: [],
      loading: false,
      savingShift: false,
      initializing: true,
      selectedFilter: null,
      userRole: null,
      userId: null,
      timePeriod: "day",
      selectedDate: null,
      customDateRange: null,
      selectedCaregiver: null,
      selectedClient: null,
      selectedPayer: null,
      selectedShiftType: null,
      selectedAgency: null,
      selectedAdmin: null,
      sortProp: "",
      sortOrder: "",
      availableCaregivers: [],
      availableClients: [],
      availableAgencies: [],
      availableAdmins: [],
      payerOptions: [
        { id: "DSPD", name: "DSPD" },
        { id: "VR", name: "VR" },
        { id: "Non Service", name: "Non Service" },
      ],
      shiftTypeOptions: [
        { id: "1-on-1", name: "1-on-1" },
        { id: "Group", name: "Group" },
      ],
      showShiftModal: false,
      shiftBeingEdited: null,
      loadDebounceTimer: null,
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      shiftColumns: [
        { key: "date", label: "Date" },
        { key: "time", label: "Time", sortable: false },
        { key: "client", label: "Client", sortKey: "client_name" },
        { key: "caregiver", label: "Caregiver" },
        { key: "service_code", label: "Code", class: "hide-on-small" },
        { key: "shift_status", label: "Status" },
        {
          key: "shift_notes",
          label: "Notes",
          width: "300px",
          class: "hide-on-medium",
          sortable: false,
        },
        { key: "actions", label: "Actions", sortable: false },
      ],
      showClockInModal: false,
      showClockOutModal: false,
      showAdminClockOutModal: false,
      shiftToAdminClockOut: null,
      showStartBreakModal: false,
      showGroupTasksModal: false,
      groupTasksShift: null,
      showEditClockInTimeModal: false,
      showEditClockOutTimeModal: false,
      showEditShiftNotesModal: false,
      showApproveShiftModal: false,
      showApproveMissedShiftModal: false,
      showChangeServiceCodeModal: false,
      showShiftHistoryModal: false,
      currentShift: null,
      shiftToEditClockInTime: null,
      shiftToEditClockOutTime: null,
      shiftToEditNotes: null,
      shiftToApprove: null,
      shiftToChangeServiceCode: null,
      shiftToApproveMissed: null,
      shiftForHistory: null,
      shiftInstanceDate: null,
      shiftHistory: [],
      loadingHistory: false,
      clockingIn: false,
      clockingOut: false,
      gettingLocation: false,
      currentLocation: null,
      selectedGroupClients: [],
      clockOutNotes: "",
      clockOutNotesError: "",

      // VR distance flagging
      isVRDistanceFlagged: false,

      // Recurring shift delete dialog
      deleteRecurringDialog: { visible: false, shift: null },
    };
  },
  computed: {
    canCreateShift() {
      // Only Admins, Leads, and Employment Specialists can create shifts
      return (
        this.userRole === "Admin" ||
        this.userRole === "Lead" ||
        this.userRole === "Employment Specialist"
      );
    },
    customDateFormat() {
      return this.dateFormat;
    },
    showFilters() {
      return this.userRole === "Admin" || this.userRole === "Lead";
    },
    datePickerType() {
      switch (this.timePeriod) {
        case "day":
          return "date";
        case "week":
          return "week";
        case "month":
          return "month";
        default:
          return "date";
      }
    },
    dateFormat() {
      switch (this.timePeriod) {
        case "day":
          return "MMMM d, yyyy";
        case "week":
          return "MMMM d, yyyy";
        case "month":
          return "MMMM yyyy";
        default:
          return "MMMM d, yyyy";
      }
    },
    placeholderText() {
      switch (this.timePeriod) {
        case "day":
          return "Select date";
        case "week":
          return "Select week";
        case "month":
          return "Select month";
        default:
          return "Select date";
      }
    },
    dateRangeDisplay() {
      if (!this.selectedDate) return "";

      let date;
      if (this.selectedDate instanceof Date) {
        date = new Date(this.selectedDate.getTime());
      } else if (typeof this.selectedDate === "string") {
        // Parse as local date to avoid timezone issues
        const [year, month, day] = this.selectedDate.split("-").map(Number);
        date = new Date(year, month - 1, day);
      } else {
        return "";
      }

      if (isNaN(date.getTime())) return "";

      const monthNamesFull = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthNamesShort = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      try {
        switch (this.timePeriod) {
          case "day":
            return `${monthNamesFull[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

          case "week":
            const dayOfWeek = date.getDay();
            const sunday = new Date(date.getTime());
            sunday.setDate(date.getDate() - dayOfWeek);
            const saturday = new Date(sunday.getTime());
            saturday.setDate(sunday.getDate() + 6);

            const sundayStr = `${dayNames[0]}, ${monthNamesShort[sunday.getMonth()]} ${sunday.getDate()}, ${sunday.getFullYear()}`;
            const saturdayStr = `${dayNames[6]}, ${monthNamesShort[saturday.getMonth()]} ${saturday.getDate()}, ${saturday.getFullYear()}`;

            return `${sundayStr} - ${saturdayStr}`;

          case "month":
            return `${monthNamesFull[date.getMonth()]} ${date.getFullYear()}`;

          default:
            return date.toLocaleDateString();
        }
      } catch (error) {
        this.$notifyError("Error displaying date range");
        return "Invalid Date";
      }
    },
    filters() {
      const statuses = [];

      // Only show Unassigned option for Admins and Leads
      if (this.userRole === "Admin" || this.userRole === "Lead") {
        statuses.push({ id: "unassigned", name: "Unassigned" });
      }

      statuses.push(
        { id: "scheduled", name: "Scheduled" },
        { id: "clocked-in", name: "Clocked In" },
        { id: "clocked-out", name: "Clocked Out" },
        { id: "approved", name: "Approved" },
        { id: "missed", name: "Missed" },
        { id: "canceled", name: "Canceled" },
      );

      return statuses;
    },
    periodOptions() {
      return [
        { id: "day", name: "Day" },
        { id: "week", name: "Week" },
        { id: "month", name: "Month" },
        { id: "custom", name: "Custom" },
      ];
    },
    payerFilterOptions() {
      return this.payerOptions;
    },
    shiftTypeFilterOptions() {
      return this.shiftTypeOptions;
    },
    caregiverFilterOptions() {
      return this.availableCaregivers;
    },
    clientFilterOptions() {
      return this.availableClients;
    },
    agencyFilterOptions() {
      return this.availableAgencies;
    },
    adminFilterOptions() {
      return this.availableAdmins;
    },
    isDSPDClient() {
      if (
        !this.currentShift?.clients ||
        this.currentShift.clients.length === 0
      ) {
        return false;
      }
      return this.currentShift.clients[0].payer_type === "DSPD";
    },
    isVRShift() {
      if (!this.currentShift) {
        return false;
      }
      return this.currentShift.payer === "VR";
    },
  },
  async mounted() {
    await Promise.all([
      this.loadShifts(),
      this.loadCaregivers(true),
      this.loadClients(),
      this.loadAgencies(),
      this.loadAdmins(),
    ]);
    this.$nextTick(() => {
      this.updateDatePickerDisplay();
      // Set flag to indicate component is fully mounted and initialization is complete
      this._isMounted = true;
      this.initializing = false;

      // Check if we have a shiftId query parameter (from navigating from Caregivers page)
      this.handleShiftIdQueryParam();
    });

    // Listen for clock in/out events from navbar
    EventBus.$on(EVENTS.SHIFT_CLOCK_IN, this.handleClockInEvent);
    EventBus.$on(EVENTS.SHIFT_CLOCK_OUT, this.handleClockOutEvent);

    // Re-render rows when offline-queued clock events change (enqueued while
    // disconnected or drained after reconnecting) so "Pending sync" badges
    // and clock buttons stay accurate.
    this._unsubscribeOfflineQueue = onQueueChange(() => {
      if (this._isMounted) this.loadShifts();
    });
  },
  beforeDestroy() {
    // Clean up event listeners
    EventBus.$off(EVENTS.SHIFT_CLOCK_IN, this.handleClockInEvent);
    EventBus.$off(EVENTS.SHIFT_CLOCK_OUT, this.handleClockOutEvent);
    if (this._unsubscribeOfflineQueue) this._unsubscribeOfflineQueue();
  },
  watch: {
    timePeriod: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
          this.updateDatePickerDisplay();
        }
      },
    },
    selectedDate: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
          this.updateDatePickerDisplay();
        }
      },
    },
    customDateRange: {
      handler(newValue) {
        if (!this.initializing && newValue && newValue.length === 2) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedCaregiver: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedClient: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedPayer: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedShiftType: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedAgency: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedAdmin: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
    selectedFilter: {
      handler() {
        if (!this.initializing) {
          this.debouncedLoadShifts();
        }
      },
    },
  },
  methods: {
    handleClockInEvent() {
      // Refresh shifts when clock in occurs from navbar
      this.loadShifts();
    },
    handleClockOutEvent() {
      // Refresh shifts when clock out occurs from navbar
      this.loadShifts();
    },
    handleShiftIdQueryParam() {
      // Check if there's a shiftId in the query parameters
      const shiftId = this.$route.query.shiftId;
      if (!shiftId) {
        return;
      }

      // Find the shift in the current shifts data
      const shift = this.shifts.find((s) => s.id === parseInt(shiftId));
      if (!shift) {
        this.$notifyError(
          "Shift not found. It may not be visible with current filters.",
        );
        // Clear the query param
        this.$router.replace({ query: {} });
        return;
      }

      // Check the shift status to determine which action to take
      const status = this.getShiftStatus(shift);

      if (status === "missed") {
        // For missed shifts, show options: Edit or Approve
        this.$confirm(
          "This is a missed shift. What would you like to do?",
          "Missed Shift Actions",
          {
            distinguishCancelAndClose: true,
            confirmButtonText: "Approve Missed Shift",
            cancelButtonText: "Edit Shift",
            type: "info",
          },
        )
          .then(() => {
            // User clicked "Approve Missed Shift"
            this.handleApproveMissedShift(shift);
          })
          .catch((action) => {
            if (action === "cancel") {
              // User clicked "Edit Shift"
              this.editShift(shift);
            }
            // If action === 'close', user closed the dialog, do nothing
          })
          .finally(() => {
            // Clear the query param
            this.$router.replace({ query: {} });
          });
      } else {
        // For other shifts, just open the edit modal
        this.editShift(shift);
        // Clear the query param
        this.$router.replace({ query: {} });
      }
    },
    debouncedLoadShifts() {
      if (this.loadDebounceTimer) {
        clearTimeout(this.loadDebounceTimer);
      }
      this.loadDebounceTimer = setTimeout(() => {
        this.loadShifts();
      }, 100);
    },
    updateDatePickerDisplay() {
      this.$nextTick(() => {
        try {
          const input = this.$refs.datePicker?.$el?.querySelector("input");
          if (input && this.dateRangeDisplay) {
            input.value = this.dateRangeDisplay;
          }
        } catch (error) {
          this.$notifyError("Error updating date picker display");
        }
      });
    },
    async loadShifts(pageNumber = 1) {
      this.$spinner.show();
      this.loading = true;
      try {
        const { startDate, endDate } = this.getDateRange();

        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("startDate", startDate);
        params.append("endDate", endDate);
        params.append("userType", this.userRole);
        params.append("userId", this.userId);

        if (this.selectedCaregiver) {
          params.append("caregiverId", this.selectedCaregiver);
        }

        if (this.selectedClient) {
          params.append("clientId", this.selectedClient);
        }

        if (this.selectedPayer) {
          params.append("payer", this.selectedPayer);
        }

        if (this.selectedShiftType) {
          params.append("shiftType", this.selectedShiftType);
        }

        if (this.selectedAgency) {
          params.append("agencyId", this.selectedAgency);
        }

        if (this.selectedAdmin) {
          params.append("adminId", this.selectedAdmin);
        }

        if (this.selectedFilter) {
          params.append("status", this.selectedFilter);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await shiftsService.getByRole(params);

        // Overlay offline-queued clock events so rows reflect what this
        // caregiver did while disconnected (adds _pendingSync markers).
        this.shifts = (response.shifts || []).map((s) =>
          applyPendingClockEvents(s, this.userId),
        );
        this.pagination = response.pagination || {
          total: this.shifts.length,
          perPage: this.pagination.perPage,
          currentPage: pageNumber,
          totalPages: Math.ceil(this.shifts.length / this.pagination.perPage),
        };
      } catch (error) {
        this.$notifyError("Error loading shifts: " + error);
      } finally {
        this.loading = false;
        this.$spinner.hide();
      }
    },
    async onClockInSuccess(response) {
      await this.loadShifts();
      // Emit global event to notify TopNavbar to refresh
      this.$root.$emit("shift-saved");
    },
    async onClockOutSuccess(response) {
      await this.loadShifts();
      // Emit global event to notify TopNavbar to refresh
      this.$root.$emit("shift-saved");
    },
    getDateRange() {
      // Handle custom date range
      if (this.timePeriod === "custom") {
        if (this.customDateRange && this.customDateRange.length === 2) {
          const [startStr, endStr] = this.customDateRange;
          const [startYear, startMonth, startDay] = startStr.split("-").map(Number);
          const [endYear, endMonth, endDay] = endStr.split("-").map(Number);

          const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
          const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

          const formatLocalDateTime = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const seconds = String(date.getSeconds()).padStart(2, "0");
            const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
          };

          return {
            startDate: formatLocalDateTime(startDate),
            endDate: formatLocalDateTime(endDate),
          };
        }
        // Return today's date range if custom range not set
        const today = new Date();
        const formatLocalDateTime = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
        };
        return {
          startDate: formatLocalDateTime(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0)),
          endDate: formatLocalDateTime(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)),
        };
      }

      let dateObj = this.selectedDate || new Date();

      if (typeof dateObj === "string") {
        const [year, month, day] = dateObj.split("-").map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      if (isNaN(dateObj.getTime())) {
        dateObj = new Date();
      }

      let startDate, endDate;

      switch (this.timePeriod) {
        case "day":
          startDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate(),
            0,
            0,
            0,
            0,
          );
          endDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate(),
            23,
            59,
            59,
            999,
          );
          break;
        case "week":
          const dayOfWeek = dateObj.getDay();
          startDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate() - dayOfWeek,
            0,
            0,
            0,
            0,
          );
          endDate = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate() + 6,
            23,
            59,
            59,
            999,
          );
          break;
        case "month":
          startDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            1,
            0,
            0,
            0,
            0,
          );
          endDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth() + 1,
            0,
            23,
            59,
            59,
            999,
          );
          break;
        default:
          startDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate(),
            0,
            0,
            0,
            0,
          );
          endDate = new Date(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate(),
            23,
            59,
            59,
            999,
          );
      }

      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
      };

      return {
        startDate: formatLocalDateTime(startDate),
        endDate: formatLocalDateTime(endDate),
      };
    },
    handlePeriodChange(value) {
      this.timePeriod = value;
      if (value === "custom") {
        // Clear custom date range - wait for user to select
        this.customDateRange = null;
      } else {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        this.selectedDate = `${year}-${month}-${day}`;
      }
    },
    goToPrevious() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === "string") {
        const [year, month, day] = dateObj.split("-").map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      let newDate;
      if (this.timePeriod === "day") {
        newDate = new Date(dateObj);
        newDate.setDate(dateObj.getDate() - 1);
      } else if (this.timePeriod === "week") {
        newDate = new Date(dateObj);
        newDate.setDate(dateObj.getDate() - 7);
      } else if (this.timePeriod === "month") {
        newDate = new Date(dateObj);
        newDate.setMonth(dateObj.getMonth() - 1);
      }

      if (newDate) {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        this.selectedDate = `${year}-${month}-${day}`;

        this.$nextTick(() => {
          this.updateDatePickerDisplay();
        });
      }
    },
    goToNext() {
      if (!this.selectedDate) return;

      let dateObj = this.selectedDate;
      if (typeof dateObj === "string") {
        const [year, month, day] = dateObj.split("-").map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (!(dateObj instanceof Date)) {
        dateObj = new Date(dateObj);
      }

      let newDate;
      if (this.timePeriod === "day") {
        newDate = new Date(dateObj);
        newDate.setDate(dateObj.getDate() + 1);
      } else if (this.timePeriod === "week") {
        newDate = new Date(dateObj);
        newDate.setDate(dateObj.getDate() + 7);
      } else if (this.timePeriod === "month") {
        newDate = new Date(dateObj);
        newDate.setMonth(dateObj.getMonth() + 1);
      }

      if (newDate) {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, "0");
        const day = String(newDate.getDate()).padStart(2, "0");
        this.selectedDate = `${year}-${month}-${day}`;

        this.$nextTick(() => {
          this.updateDatePickerDisplay();
        });
      }
    },
    goToToday() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      this.selectedDate = `${year}-${month}-${day}`;

      this.$nextTick(() => {
        this.updateDatePickerDisplay();
      });

      let message = "";

      if (this.timePeriod === "day") {
        message = "Navigated to today's date";
      } else if (this.timePeriod === "week") {
        message = "Navigated to the week containing today's date";
      } else if (this.timePeriod === "month") {
        message = "Navigated to the month containing today's date";
      }

      this.$notifySuccess(message);
    },
    async loadCaregivers(includeAdmins = false) {
      try {
        let caregivers = [];

        if (this.userRole === "Admin" || this.userRole === "Lead") {
          const params = new URLSearchParams();
          if (includeAdmins) {
            params.append("includeAdmins", "true");
          }
          const response = await caregiversService.list(params);
          caregivers = response || [];
        } else if (this.userRole === "Employment Specialist") {
          // Employment Specialists can only see themselves in caregiver dropdowns
          const params = new URLSearchParams();
          if (includeAdmins) {
            params.append("includeAdmins", "true");
          }
          const response = await caregiversService.list(params);
          const allCaregivers = response || [];
          caregivers = allCaregivers.filter((c) => c.id === this.userId);
        }

        // Backend now handles the filtering based on includeAdmins parameter
        this.availableCaregivers = caregivers.map((caregiver) => ({
          id: Number(caregiver.id) || caregiver.id,
          name: `${caregiver.first_name} ${caregiver.last_name}`,
          user_type: caregiver.user_type, // Include user_type for future reference
        }));
      } catch (error) {
        this.$notifyError("Error loading caregivers: " + error);
        this.availableCaregivers = []; // Ensure it's always an array
      }
    },
    async loadClients() {
      try {
        if (
          this.userRole === "Admin" ||
          this.userRole === "Lead" ||
          this.userRole === "Employment Specialist"
        ) {
          const response = await clientsService.list({ slim: true });
          this.availableClients = (response || []).map((client) => ({
            id: Number(client.id) || client.id,
            name: `${client.first_name} ${client.last_name}`,
          }));
        }
      } catch (error) {
        this.$notifyError("Error loading clients: " + error);
        this.availableClients = []; // Ensure it's always an array
      }
    },
    async loadAgencies() {
      try {
        const response = await officesService.list();
        this.availableAgencies = (response || []).map((office) => ({
          id: Number(office.id) || office.id,
          name: office.name,
        }));
      } catch (error) {
        this.$notifyError("Error loading agencies: " + error);
        this.availableAgencies = []; // Ensure it's always an array
      }
    },
    async loadAdmins() {
      try {
        // Only Admins can see the admin filter
        if (this.userRole !== "Admin") {
          this.availableAdmins = [];
          return;
        }

        const response = await caregiversService.getAdmins();
        // Backend already returns formatted data with id and name
        this.availableAdmins = (response || []).map((admin) => ({
          id: Number(admin.id) || admin.id,
          name: admin.name,
        }));
      } catch (error) {
        this.$notifyError("Error loading admins: " + error);
        this.availableAdmins = []; // Ensure it's always an array
      }
    },
    handleStatusChange(value) {
      this.selectedFilter = value;
      this.pagination.currentPage = 1;
    },
    handlePayerChange(value) {
      this.selectedPayer = value;
    },
    handleShiftTypeChange(value) {
      this.selectedShiftType = value;
    },
    handleCaregiverChange(value) {
      this.selectedCaregiver = value;
    },
    handleClientChange(value) {
      this.selectedClient = value;
    },
    handleAgencyChange(value) {
      this.selectedAgency = value;
    },
    handleAdminChange(value) {
      this.selectedAdmin = value;
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.loadShifts(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.loadShifts(1);
    },
    formatTableDate(dateStr) {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    },
    getShiftStatus(shift) {
      if (!shift) return "scheduled";
      if (shift.isPotential) {
        return "scheduled";
      }

      // For group shifts, check if the current user is an assigned caregiver
      // Use shift_type === 'Group' (not isDSPDGroupShift) so this works even without service_codes
      if (shift.shift_type === 'Group' && shift.caregivers && shift.caregivers.length > 0) {
        const isAssigned = shift.caregivers.some(c => Number(c.id) === Number(this.userId));
        if (isAssigned) {
          // Show the current user's own individual status from caregiverClockTimes
          const clockTimes = shift.caregiverClockTimes || [];
          const myClockTime = clockTimes.find(ct => Number(ct.id_users) === Number(this.userId));
          const overallStatus = (shift.shift_status || shift.status || '').toLowerCase();
          const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
          const today = new Date();
          const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
          const dayPast = shiftDate && shiftDate < todayStr;

          if (!myClockTime || !myClockTime.clock_in) {
            if (overallStatus === 'approved') return 'approved';
            if (overallStatus === 'missed') return 'missed';
            if (dayPast) return 'missed';
            return 'scheduled';
          } else if (myClockTime.clock_in && !myClockTime.clock_out) {
            if (dayPast) return 'error';
            return 'clocked-in';
          } else if (myClockTime.clock_out) {
            if (overallStatus === 'approved') return 'approved';
            return 'clocked-out';
          }
          return 'scheduled';
        }
        // Not assigned - for DSPD group shifts, derive overall status from all caregivers
        if (isDSPDGroupShift(shift)) {
          const statuses = shift.caregivers.map(c => getCaregiverShiftStatus(shift, c.id));
          if (statuses.some(s => s === 'error')) return 'error';
          if (statuses.every(s => s === 'approved')) return 'approved';
          if (statuses.every(s => s === 'missed')) return 'missed';
          if (statuses.every(s => s === 'missed' || s === 'error')) return 'error';
          if (statuses.some(s => s === 'clocked-in')) return 'clocked-in';
          if (statuses.some(s => s === 'clocked-out')) return 'clocked-out';
          return 'scheduled';
        }
      }

      // Non-group shifts where the current user is a shadow/training
      // assignee (or already has a side clock row): their status is their
      // own record — the shift-level status belongs to the primary.
      if ((shift.shift_type || "1-on-1") !== "Group") {
        const mine = (shift.caregivers || []).find(
          (c) => Number(c.id) === Number(this.userId),
        );
        const hasMySideRow = (shift.caregiverClockTimes || []).some(
          (ct) => Number(ct.id_users) === Number(this.userId) && ct.clock_in,
        );
        if ((mine && (mine.role || "primary") !== "primary") || hasMySideRow) {
          return getCaregiverShiftStatus(shift, this.userId);
        }
      }

      // For non-group shifts, use the overall shift status
      const status = shift.shift_status || shift.status;
      if (status) {
        if (status.toLowerCase() === "unassigned") return "unassigned";
        if (status.toLowerCase() === "approved") return "approved";
        if (status.toLowerCase() === "clocked out") return "clocked-out";
        if (status.toLowerCase() === "clocked in") return "clocked-in";
        if (status.toLowerCase() === "missed") return "missed";
        if (status.toLowerCase() === "error") return "error";
        if (status.toLowerCase() === "canceled") return "canceled";
      }

      // No status set — check if the shift date has passed
      const shiftDate = (shift.date || shift.scheduled_date || '').split('T')[0];
      if (shiftDate) {
        const today = new Date();
        const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
        if (shiftDate < todayStr) {
          if (!shift.clockIn && !shift.clock_in) {
            return 'missed';
          }
          return 'error';
        }
      }

      const hasCaregivers = shift.caregivers && shift.caregivers.length > 0;
      if (!hasCaregivers && (!status || status.toLowerCase() === "scheduled")) {
        return "unassigned";
      }

      if (shift.clockOut || shift.clock_out) {
        return "clocked-out";
      }
      if (shift.clockIn || shift.clock_in) {
        return "clocked-in";
      }

      return "scheduled";
    },
    formatStatus(status) {
      if (!status) return "Scheduled";

      const statusMap = {
        scheduled: "Scheduled",
        unassigned: "Unassigned",
        "clocked-in": "Clocked In",
        "clocked-out": "Clocked Out",
        approved: "Approved",
        missed: "Missed",
        error: "Error",
        canceled: "Canceled",
      };

      return statusMap[status.toLowerCase()] || status;
    },
    getStatusClass(status) {
      if (!status) return "badge-info";

      const statusLower = status.toLowerCase();

      if (statusLower === "approved") return "badge-primary";
      if (statusLower === "clocked-in") return "badge-success";
      if (statusLower === "clocked-out") return "badge-warning";
      if (statusLower === "scheduled") return "badge-info";
      if (statusLower === "unassigned") return "badge-secondary";
      if (statusLower === "missed") return "badge-missed";
      if (statusLower === "error") return "badge-danger";
      if (statusLower === "canceled") return "badge-canceled";

      return "badge-info";
    },
    calculateTotalTime(shift) {
      if (!shift) return "N/A";

      if (shift.clockIn && shift.clockOut) {
        const start = new Date(`1970-01-01T${shift.clockIn}`);
        const end = new Date(`1970-01-01T${shift.clockOut}`);
        const diffMs = end - start;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
      }

      const startTime = shift.scheduled_start || shift.startTime;
      const endTime = shift.scheduled_end || shift.endTime;

      if (startTime && endTime) {
        const start = new Date(`1970-01-01T${startTime}`);
        const end = new Date(`1970-01-01T${endTime}`);
        const diffMs = end - start;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const suffix = shift.isPotential ? " (potential)" : " (scheduled)";
        return `${hours}h ${minutes}m${suffix}`;
      }
      return "N/A";
    },
    formatClient(shift) {
      if (!shift) return "Unassigned";
      // Non Service shifts don't require a client
      if (shift.payer === "Non Service") {
        return "N/A";
      }
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return "Unassigned";
    },
    formatCaregiver(shift) {
      if (!shift) return "Unassigned";
      if (shift.caregivers && shift.caregivers.length > 0) {
        // Show the primary caregiver; shadow/training are in the tooltip
        const primary = primaryCaregiverOf(shift) || shift.caregivers[0];
        return primary.name;
      }
      return "Unassigned";
    },
    hasMultipleClients(shift) {
      return shift && shift.clients && shift.clients.length > 1;
    },
    hasMultipleCaregivers(shift) {
      return shift && shift.caregivers && shift.caregivers.length > 1;
    },
    formatMultipleCaregivers(shift) {
      const caregivers = shift.caregivers || [];
      // Non-group shifts with shadow/training extras: primary + count
      if ((shift.shift_type || "1-on-1") !== "Group") {
        const extras = caregivers.filter(
          (c) => (c.role || "primary") !== "primary",
        );
        if (extras.length > 0) {
          const primary = primaryCaregiverOf(shift) || caregivers[0];
          return `${primary.name} +${extras.length} shadow`;
        }
      }
      return `${caregivers.length} caregivers`;
    },
    // True when a group shift has any per-caregiver clock location recorded
    // (group coordinates live on caregiverClockTimes, not on the shift row).
    rowHasCaregiverClockLocations(shift) {
      const clockTimes = shift && shift.caregiverClockTimes;
      return (
        Array.isArray(clockTimes) &&
        clockTimes.some((ct) => ct.clock_in_geocode || ct.clock_out_geocode)
      );
    },
    isGroupShiftWithCaregivers(shift) {
      return isDSPDGroupShift(shift) && shift.caregivers && shift.caregivers.length > 0 && (this.userRole === 'Admin' || this.userRole === 'Lead');
    },
    formatCaregiverStatusHtml(shift) {
      return formatCaregiverStatusHtml(shift, shift.caregivers);
    },
    getCaregiverBadgeClass(shift, caregiverId) {
      const status = getCaregiverShiftStatus(shift, caregiverId);
      switch (status) {
        case 'clocked-in': return 'dot-clocked-in';
        case 'clocked-out': return 'dot-clocked-out';
        case 'approved': return 'dot-approved';
        case 'missed': return 'dot-missed';
        case 'unassigned': return 'dot-unassigned';
        default: return 'dot-scheduled';
      }
    },
    getClientTooltipHtml(shift) {
      if (!shift || !shift.clients || shift.clients.length === 0) {
        return "";
      }
      return shift.clients.map(client => client.name).join('<br>');
    },
    getCaregiverTooltipHtml(shift) {
      if (!shift || !shift.caregivers || shift.caregivers.length === 0) {
        return "";
      }
      if (isDSPDGroupShift(shift) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        return formatCaregiverStatusHtml(shift, shift.caregivers);
      }
      return shift.caregivers.map(caregiver => caregiverNameWithRole(caregiver)).join('<br>');
    },
    getClientTooltip(shift) {
      if (!shift || !shift.clients || shift.clients.length === 0) {
        return "";
      }
      return shift.clients.map(client => client.name).join("\n");
    },
    getCaregiverTooltip(shift) {
      if (!shift || !shift.caregivers || shift.caregivers.length === 0) {
        return "";
      }

      // For DSPD group shifts, show individual caregiver statuses
      if (isDSPDGroupShift(shift) && (this.userRole === 'Admin' || this.userRole === 'Lead')) {
        return shift.caregivers.map(caregiver => {
          const status = getCaregiverShiftStatus(shift, caregiver.id);
          const statusIcon = getStatusIcon(status);
          return `${statusIcon} ${caregiver.name}`;
        }).join("\n");
      }

      return shift.caregivers.map(caregiver => caregiver.name).join("\n");
    },
    formatServiceCode(shift) {
      if (!shift) return "Not specified";

      // Check authorization service code first
      if (shift.authorization && shift.authorization.service_code) {
        return shift.authorization.service_code.code || "Not specified";
      }

      // For Non Service shifts, check non_service_codes array
      if (
        shift.payer === "Non Service" &&
        shift.non_service_codes &&
        shift.non_service_codes.length > 0
      ) {
        const codes = shift.non_service_codes
          .map((sc) => sc.code)
          .filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      // Fallback to direct service_codes array if available
      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      return "Not specified";
    },
    createShift() {
      if (!this.canCreateShift) {
        this.$notifyError("You don't have permission to create shifts");
        return;
      }
      this.shiftBeingEdited = null;
      this.showShiftModal = true;
    },
    async editShift(shift) {
      if (!this.canEditShift(shift)) {
        this.$notifyError("You don't have permission to edit this shift");
        return;
      }

      const status = (shift.status || shift.shift_status)?.toLowerCase();

      // Check if shift has clock times (even if status field hasn't been updated)
      const hasClockIn = !!(shift.clockIn || shift.clock_in);
      const hasClockOut = !!(shift.clockOut || shift.clock_out);

      const isClocked =
        status === "clocked-in" ||
        status === "clocked in" ||
        status === "clocked_in" ||
        (hasClockIn && !hasClockOut);
      const isClockedOut =
        status === "clocked-out" ||
        status === "clocked out" ||
        status === "clocked_out" ||
        status === "clockedout" ||
        status === "error" ||
        (status?.includes("clocked") && status?.includes("out")) ||
        (hasClockIn && hasClockOut);
      const isMissed = status === "missed";

      // Approved shifts (Admins only, per canEditShift): documentation-only
      // edit via the notes modal. It never touches clock times, and its
      // payer/client/service-code selectors are locked for approved shifts.
      if (status === "approved") {
        this.shiftToEditNotes = {
          ...shift,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          caregivers: shift.caregivers,
          clients: shift.clients,
          service_codes: shift.service_codes,
          originalData: {
            ...shift,
            clockIn: shift.clock_in || shift.clockIn,
            clock_in: shift.clock_in || shift.clockIn,
            scheduled_start: shift.scheduled_start,
            scheduled_end: shift.scheduled_end,
            clients: shift.clients,
          },
        };
        this.showEditShiftNotesModal = true;
        return;
      }

      // Handle missed shifts
      if (isMissed) {
        const mappedShift = {
          ...shift,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          originalData: {
            ...shift,
            scheduled_start: shift.scheduled_start,
            scheduled_end: shift.scheduled_end,
          },
        };

        // Check if the current user is one of the assigned caregivers
        const caregivers = shift.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(this.userId)
        );

        // Caregivers and Employment Specialists can only edit notes and activities
        if (
          this.userRole === "Caregiver" ||
          this.userRole === "Employment Specialist"
        ) {
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
          return;
        }

        // Admins and Leads who are assigned to the shift can only edit notes and activities
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          isAssignedCaregiver
        ) {
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
          return;
        }

        // Admins and Leads who are NOT assigned can edit the full shift details
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          // Check if the assigned caregiver is an admin
          let hasAdminCaregiver = false;
          if (shift.caregivers && shift.caregivers.length > 0) {
            const caregiverType = shift.caregivers[0].user_type;
            hasAdminCaregiver =
              caregiverType === "Admin" || caregiverType === "Lead";
          }

          // If the shift has an admin caregiver, reload caregivers with admins included
          if (hasAdminCaregiver) {
            await this.loadCaregivers(true);
            this.shiftBeingEdited = { ...shift };
            await this.$nextTick();
            this.showShiftModal = true;
          } else {
            this.shiftBeingEdited = { ...shift };
            this.showShiftModal = true;
          }
          return;
        }
      }

      if (isClocked) {
        const mappedShift = {
          ...shift,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          // Ensure group shift fields are preserved at top level
          clientDetails: shift.clientDetails,
          caregiverClockTimes: shift.caregiverClockTimes,
          caregivers: shift.caregivers,
          clients: shift.clients,
          service_codes: shift.service_codes,
          originalData: {
            ...shift,
            clockIn: shift.clock_in || shift.clockIn,
            clock_in: shift.clock_in || shift.clockIn,
            scheduled_start: shift.scheduled_start,
            scheduled_end: shift.scheduled_end,
            clientDetails: shift.clientDetails,
            caregiverClockTimes: shift.caregiverClockTimes,
            clients: shift.clients,
          },
        };

        // For group shifts, admins can edit caregiver assignments via the EditClockInTimeModal
        if (shift.shift_type === "Group" && this.userRole === "Admin") {
          this.shiftToEditClockInTime = mappedShift;
          this.showEditClockInTimeModal = true;
          return;
        }

        // Check if the current user is one of the assigned caregivers
        const caregivers = shift.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(this.userId)
        );

        // Determine which modal to show based on role and assignment
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          // Admins and Leads can edit clock in time for other caregivers' shifts
          this.shiftToEditClockInTime = mappedShift;
          this.showEditClockInTimeModal = true;
        } else {
          // Admins/Leads editing their own shifts, Caregivers, and Employment Specialists can only edit notes and activities
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
        }
        return;
      }

      if (isClockedOut) {
        const mappedShift = {
          ...shift,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          // Ensure group shift fields are preserved at top level
          clientDetails: shift.clientDetails,
          caregiverClockTimes: shift.caregiverClockTimes,
          caregivers: shift.caregivers,
          clients: shift.clients,
          service_codes: shift.service_codes,
          originalData: {
            ...shift,
            clockIn: shift.clock_in || shift.clockIn,
            clock_in: shift.clock_in || shift.clockIn,
            clockOut: shift.clock_out || shift.clockOut,
            clock_out: shift.clock_out || shift.clockOut,
            scheduled_start: shift.scheduled_start,
            scheduled_end: shift.scheduled_end,
            clientDetails: shift.clientDetails,
            caregiverClockTimes: shift.caregiverClockTimes,
            clients: shift.clients,
          },
        };

        // For group shifts, admins can edit caregiver assignments via the EditClockOutTimeModal
        if (shift.shift_type === "Group" && this.userRole === "Admin") {
          this.shiftToEditClockOutTime = mappedShift;
          this.showEditClockOutTimeModal = true;
          return;
        }

        // Check if the current user is one of the assigned caregivers
        const caregivers = shift.caregivers || [];
        const isAssignedCaregiver = caregivers.some(
          (c) => Number(c.id) === Number(this.userId)
        );

        // Determine which modal to show based on role and assignment
        if (
          (this.userRole === "Admin" || this.userRole === "Lead") &&
          !isAssignedCaregiver
        ) {
          // Admins and Leads can edit clock times for other caregivers' shifts
          this.shiftToEditClockOutTime = mappedShift;
          this.showEditClockOutTimeModal = true;
        } else {
          // Admins/Leads editing their own shifts, Caregivers, and Employment Specialists can only edit notes and activities
          this.shiftToEditNotes = mappedShift;
          this.showEditShiftNotesModal = true;
        }
        return;
      }
      // Check if the assigned caregiver is an admin
      let hasAdminCaregiver = false;
      if (shift.caregivers && shift.caregivers.length > 0) {
        const caregiverType = shift.caregivers[0].user_type;
        hasAdminCaregiver =
          caregiverType === "Admin" || caregiverType === "Lead";
      }

      // If the shift has an admin caregiver, reload caregivers with admins included
      if (hasAdminCaregiver) {
        await this.loadCaregivers(true);
        // Set the shift data first so the modal can access it
        this.shiftBeingEdited = { ...shift };
        // Wait for Vue to update the DOM
        await this.$nextTick();
        // Now show the modal
        this.showShiftModal = true;
      } else {
        // For non-admin caregivers, proceed normally
        this.shiftBeingEdited = { ...shift };
        this.showShiftModal = true;
      }
    },
    closeShiftModal() {
      this.showShiftModal = false;
      this.shiftBeingEdited = null;
    },
    processSingleShift(shift, isEditing) {
      // Preserve existing status if it's "missed", otherwise default to "Scheduled"
      let shiftStatus = "Scheduled";
      if (isEditing && this.shiftBeingEdited) {
        const currentStatus =
          this.shiftBeingEdited.shift_status || this.shiftBeingEdited.status;
        if (currentStatus && currentStatus.toLowerCase() === "missed") {
          shiftStatus = "Missed";
        } else if (currentStatus && currentStatus !== "Scheduled") {
          shiftStatus = currentStatus;
        }
      }

      // Convert local times to UTC datetimes for the new schema
      const convertToUTCDateTime = (timeStr, dateStr) => {
        if (!timeStr || !dateStr) return null;
        if (timeStr.includes("T")) {
          return timeStr;
        }
        const localDateTime = new Date(`${dateStr}T${timeStr}`);
        return localDateTime.toISOString();
      };

      const dateOnly = shift.recurring
        ? shift.recurring_start_date
        : shift.date
          ? shift.date.split("T")[0]
          : null;

      const shiftData = {
        ...shift,
        scheduled_start: convertToUTCDateTime(shift.scheduled_start, dateOnly),
        scheduled_end: convertToUTCDateTime(shift.scheduled_end, dateOnly),
        shift_status: shiftStatus,
        clientIds: shift.clientIds || (shift.clientId ? [shift.clientId] : []),
        caregiverIds: shift.caregiverIds || (shift.caregiverId ? [shift.caregiverId] : []),
        serviceCodeIds: shift.serviceCodeIds || shift.selectedServiceCodes || [],
      };

      if (shift.clientAddressId) {
        shiftData.clientAddressId = shift.clientAddressId;
      }
      if (shift.clientGeocode) {
        shiftData.clientGeocode = shift.clientGeocode;
      }
      if (shift.clientGeocodeAddressId) {
        shiftData.clientGeocodeAddressId = shift.clientGeocodeAddressId;
      }
      if (shift.recurring) {
        shiftData.recurring = true;
        shiftData.recurrencePattern = shift.recurrencePattern;
        shiftData.selectedWeekdays = shift.selectedWeekdays || [];
        shiftData.customDates = shift.customDates || [];
        shiftData.recurring_start_date = shift.recurring_start_date;
        shiftData.recurring_end_date = shift.recurring_end_date || null;
      }
      if (shift.vrAlternateLocation) {
        shiftData.vrAlternateLocation = shift.vrAlternateLocation;
        shiftData.useAlternateLocation = shift.useAlternateLocation;
      }
      if (shift.editEntireSeriesFromDate) {
        shiftData.editEntireSeriesFromDate = shift.editEntireSeriesFromDate;
      }
      if (shift.acknowledgeClientOverlap) {
        shiftData.acknowledgeClientOverlap = true;
      }
      if (shift.acknowledgeCaregiverConflict) {
        shiftData.acknowledgeCaregiverConflict = true;
      }

      return shiftData;
    },
    async saveShift({ shift, shifts, isEditing, isGroupShift, isDSPDGroupShift, exceptionShiftIds }) {
      this.savingShift = true;
      try {
        // Handle DSPD group shifts (single shift with multiple clients and task assignments)
        if (isDSPDGroupShift && shift) {
          // For DSPD group shifts, process as a single shift with client details
          const processedShift = this.processSingleShift(shift, isEditing);
          const savedShift = await shiftsService.create(processedShift);

          const clientCount = shift.clientIds?.length || 1;
          this.$notifySuccess(`DSPD group shift created successfully for ${clientCount} client(s)!`);
          this.closeShiftModal();
          await this.loadShifts();
          this.$root.$emit("shift-saved");
          return;
        }

        // Handle group shifts (multiple shifts to create)
        if (isGroupShift && shifts && Array.isArray(shifts)) {
          // Create all shifts in parallel
          const processedShifts = shifts.map(
            (shiftData) => this.processSingleShift(shiftData, false),
          );
          const createdShifts = await Promise.all(
            processedShifts.map((processed) => shiftsService.create(processed)),
          );

          this.$notifySuccess(`${createdShifts.length} group shifts created successfully!`);
          this.closeShiftModal();
          await this.loadShifts();
          this.$root.$emit("shift-saved");
          return;
        }

        // Process shift data using helper method
        const shiftData = this.processSingleShift(shift, isEditing);

        let savedShift;
        if (isEditing && !shift.isPotential) {
          savedShift = await shiftsService.update(shift.id, shiftData);
          this.$notifySuccess("Shift updated successfully!");
        } else if (isEditing && shift.isPotential) {
          savedShift = await shiftsService.create(shiftData);

          await this.recordDeletedPotentialShift(this.shiftBeingEdited);

          this.$notifySuccess("Shift updated successfully!");
        } else {
          savedShift = await shiftsService.create(shiftData);

          this.$notifySuccess("Shift created successfully!");
        }

        // Handle exception shifts if user chose to keep them
        if (exceptionShiftIds && exceptionShiftIds.length > 0 && savedShift) {
          try {
            // The service layer already unwraps the response body. A series
            // edit returns newRecurringShiftId; a plain save returns the shift.
            const newRecurringShiftId =
              savedShift.newRecurringShiftId || savedShift.id;

            if (newRecurringShiftId) {
              await this.$axios.post("/api/shifts/handle-exception-shifts", {
                action: "keep",
                exceptionShiftIds,
                newRecurringShiftId,
              });
              this.$notifySuccess(
                `Exception shifts moved to new recurring shift`,
              );
            }
          } catch (exceptionError) {
            this.$notifyError(
              "Shift saved but failed to move exception shifts",
            );
          }
        }

        this.closeShiftModal();
        await this.loadShifts();

        // Emit global event to notify TopNavbar to refresh
        this.$root.$emit("shift-saved");
      } catch (error) {
        // Client overlap is overridable: offer "Schedule anyway" and retry
        // with the acknowledgement flag (managers get notified server-side).
        // Caregiver availability is overridable too, with a confirm that
        // states the payroll/report consequences before the retry.
        if (await this.confirmCaregiverOverride(error)) {
          if (shift) shift.acknowledgeCaregiverConflict = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeCaregiverConflict = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
            exceptionShiftIds,
          });
        }
        if (await this.confirmClientOverlap(error)) {
          if (shift) shift.acknowledgeClientOverlap = true;
          (shifts || []).forEach((s) => {
            if (s) s.acknowledgeClientOverlap = true;
          });
          return this.saveShift({
            shift,
            shifts,
            isEditing,
            isGroupShift,
            isDSPDGroupShift,
            exceptionShiftIds,
          });
        }
        // Rich, itemized overlap message naming conflicting client(s)/shift(s);
        // falls back to the generic error otherwise. See conflictMessageMixin.
        this.handleShiftSaveError(error);
      } finally {
        this.savingShift = false;
      }
    },
    async deleteShift(shift) {
      try {
        if (shift.isPotential) {
          const confirmRemove = await this.$confirmAction(null, "Remove this potential shift from the schedule?");
          if (!confirmRemove) return;
          const deletedShiftData = {
            date: shift.date,
            scheduled_start: shift.scheduled_start || shift.startTime,
            scheduled_end: shift.scheduled_end || shift.endTime,
            client_id:
              shift.clients && shift.clients.length > 0
                ? shift.clients[0].id
                : null,
            caregiver_id:
              shift.caregivers && shift.caregivers.length > 0
                ? shift.caregivers[0].id
                : null,
            shift_type: shift.shift_type,
            payer: shift.payer,
            notes: `Deleted potential shift from recurring schedule`,
          };

          await deletedPotentialShiftsService.create(deletedShiftData);
          this.$notifySuccess("Potential shift removed from schedule!");
          await this.loadShifts();
        } else if (shift.recurring || shift.parent_recurring_shift_id) {
          // Show 3-option dialog for recurring shifts
          this.deleteRecurringDialog = { visible: true, shift };
        } else {
          const confirmDelete = await this.$confirmAction(null, "Are you sure you want to delete this shift?");
          if (!confirmDelete) return;
          await shiftsService.delete(shift.id);
          this.$notifySuccess("Shift deleted successfully!");
          await this.loadShifts();
        }
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },

    async deleteThisShiftOnly() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteWithDate(shift.id, shiftDate);
        this.$notifySuccess("Shift occurrence deleted successfully!");
        await this.loadShifts();
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },

    async deleteThisAndFutureShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const parentId = shift.parent_recurring_shift_id || shift.id;
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteFromDate(parentId, shiftDate);
        this.$notifySuccess("This and all future occurrences deleted successfully!");
        await this.loadShifts();
      } catch (error) {
        this.$notifyError("Error deleting shifts: " + error);
      }
    },

    async deleteAllShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const parentId = shift.parent_recurring_shift_id || shift.id;
        await shiftsService.deleteEntireSeries(parentId);
        this.$notifySuccess("Entire recurring series deleted successfully!");
        await this.loadShifts();
      } catch (error) {
        this.$notifyError("Error deleting series: " + error);
      }
    },
    getEmptyMessage() {
      if (this.loading) {
        return "Loading shifts...";
      }

      const { startDate, endDate } = this.getDateRange();
      const start = new Date(startDate);
      const end = new Date(endDate);
      const options = { month: "short", day: "numeric", year: "numeric" };

      let dateRange;
      if (startDate === endDate) {
        dateRange = start.toLocaleDateString("en-US", options);
      } else {
        dateRange = `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString("en-US", options)}`;
      }

      const activeFilters = [];
      if (this.selectedFilter) {
        const filterLabel =
          this.filters.find((f) => f.id === this.selectedFilter)?.name ||
          this.selectedFilter;
        activeFilters.push(`Status: ${filterLabel}`);
      }
      if (this.selectedPayer) {
        activeFilters.push(`Payer: ${this.selectedPayer}`);
      }
      if (this.selectedShiftType) {
        activeFilters.push(`Shift Type: ${this.selectedShiftType}`);
      }
      if (this.selectedCaregiver) {
        const caregiver = this.availableCaregivers.find(
          (c) => c.id === this.selectedCaregiver,
        );
        activeFilters.push(
          `Caregiver: ${caregiver?.name || this.selectedCaregiver}`,
        );
      }
      if (this.selectedClient) {
        const client = this.availableClients.find(
          (c) => c.id === this.selectedClient,
        );
        activeFilters.push(`Client: ${client?.name || this.selectedClient}`);
      }
      if (this.selectedAgency) {
        const agency = this.availableAgencies.find(
          (a) => a.id === this.selectedAgency,
        );
        activeFilters.push(`Agency: ${agency?.name || this.selectedAgency}`);
      }

      let message = `No shifts found for ${dateRange}`;
      if (activeFilters.length > 0) {
        message += ` with the following filters: ${activeFilters.join(", ")}`;
      }

      return message + ". Try adjusting your filters or date range.";
    },
    canClockIn(shift) {
      const status = this.getShiftStatus(shift);
      const isLinkedToUser =
        shift.caregivers &&
        shift.caregivers.some(
          (c) =>
            Number(c.id) === Number(this.userId) ||
            String(c.id) === String(this.userId),
        );

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const shiftDateStr = shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;

      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

      const startTime = shift.scheduled_start || shift.startTime || "";
      const endTime = shift.scheduled_end || shift.endTime || "";

      if (startTime && endTime) {
        // Convert UTC times to local time for comparison
        const convertUTCTimeToLocalMinutes = (timeStr, dateStr) => {
          // Handle new format where timeStr is already a full UTC datetime
          if (timeStr && timeStr.includes("T")) {
            const utcDate = new Date(timeStr);
            if (!isNaN(utcDate.getTime())) {
              return utcDate.getHours() * 60 + utcDate.getMinutes();
            }
          }

          // Legacy format: combine date and time as local time
          const localDateTime = `${dateStr}T${timeStr}`;
          const utcDate = new Date(localDateTime);
          if (!isNaN(utcDate.getTime())) {
            return utcDate.getHours() * 60 + utcDate.getMinutes();
          }
          // Fallback: treat as local time
          const [hour, min] = timeStr.split(":").map(Number);
          return hour * 60 + min;
        };

        const startMinutes = convertUTCTimeToLocalMinutes(
          startTime,
          shiftDateStr,
        );
        const endMinutes = convertUTCTimeToLocalMinutes(endTime, shiftDateStr);

        // CMP exception: allow clock-in any time during the calendar day
        const isCMPShift = (shift.service_codes || [])
          .some(sc => sc.code === 'CMP');

        const allowedStartMinutes = startMinutes - 30;
        const isInTimeWindow = isCMPShift
          ? true
          : (currentTimeMinutes >= allowedStartMinutes &&
             currentTimeMinutes < endMinutes);

        return (
          status === "scheduled" && isLinkedToUser && isToday && isInTimeWindow
        );
      }

      return false;
    },
    canClockOut(shift) {
      const isClocked = shift.shift_status === "Clocked In";

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // Compare as strings to avoid timezone conversion issues
      const shiftDateStr = shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;

      // Check if current user is assigned to this shift
      const isAssignedToShift =
        shift.caregivers &&
        shift.caregivers.some(
          (caregiver) => Number(caregiver.id) === Number(this.userId),
        );

      // For DSPD group shifts, check this caregiver's individual status AND all tasks complete
      if (isDSPDGroupShift(shift)) {
        const caregiverStatus = getCaregiverShiftStatus(shift, this.userId);
        return caregiverStatus === 'clocked-in' && isToday && isAssignedToShift && areAllGroupTasksComplete(shift, this.userId);
      }

      return isClocked && isToday && isAssignedToShift;
    },

    // Admin/Lead override: clock out a shift on behalf of a caregiver who forgot.
    // Shown only when the normal Clock Out button would not appear for this user
    // (i.e. they're not the assigned, clocked-in caregiver), to avoid duplicates.
    canAdminClockOut(shift) {
      if (this.userRole !== "Admin" && this.userRole !== "Lead") return false;
      if (this.canClockOut(shift)) return false;

      if (isDSPDGroupShift(shift)) {
        const clockTimes = shift.caregiverClockTimes || [];
        return clockTimes.some((ct) => ct.clock_in && !ct.clock_out);
      }

      return shift.shift_status === "Clocked In";
    },

    handleAdminClockOut(shift) {
      this.shiftToAdminClockOut = shift;
      this.showAdminClockOutModal = true;
    },

    showTasksButton(shift) {
      if (!isDSPDGroupShift(shift)) return false;
      const status = getCaregiverShiftStatus(shift, this.userId);
      return status === 'clocked-in';
    },
    openGroupTasks(shift) {
      this.groupTasksShift = shift;
      this.showGroupTasksModal = true;
    },
    canStartBreak(shift) {
      // Must be clocked in
      if (shift.shift_status !== "Clocked In") {
        return false;
      }

      // Must be assigned to this shift
      const isAssignedToShift =
        shift.caregivers &&
        shift.caregivers.some(
          (caregiver) => Number(caregiver.id) === Number(this.userId),
        );
      if (!isAssignedToShift) {
        return false;
      }

      // Must be today
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const shiftDateStr = shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;
      if (!isToday) {
        return false;
      }

      // Must have JC service code
      const serviceCodes = shift.service_codes || [];
      const hasJCServiceCode = serviceCodes.some((sc) => sc.code === "JC");
      if (!hasJCServiceCode) {
        return false;
      }

      // Must be >= 4 hours
      const startTime = shift.scheduled_start || shift.startTime;
      const endTime = shift.scheduled_end || shift.endTime;
      if (!startTime || !endTime) {
        return false;
      }

      // Handle both full ISO datetime strings and time-only strings
      let startDate, endDate;
      if (startTime.includes("T")) {
        // Full ISO datetime string
        startDate = new Date(startTime);
        endDate = new Date(endTime);
      } else {
        // Time-only string, combine with date
        startDate = new Date(`${shiftDateStr}T${startTime}`);
        endDate = new Date(`${shiftDateStr}T${endTime}`);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      const durationHours = (endDate - startDate) / (1000 * 60 * 60);
      if (durationHours < 4) {
        return false;
      }

      // Check if a break has already been completed
      // If there's any break with both break_start and break_end, don't show button
      const breaks = shift.breaks || [];
      const hasCompletedBreak = breaks.some(
        (b) => b.break_start && b.break_end,
      );
      if (hasCompletedBreak) {
        return false;
      }

      return true;
    },
    canApproveShift(shift) {
      // Only Admins can approve shifts
      if (this.userRole !== "Admin") return false;

      const status = this.getShiftStatus(shift);
      // Only allow approval for clocked-out shifts
      if (status !== "clocked-out") return false;

      const caregivers = shift.caregivers || [];
      if (caregivers.length === 0) return false;

      // Admins cannot approve a shift where they are an assigned caregiver
      if (caregivers.some(c => Number(c.id) === Number(this.userId))) {
        return false;
      }

      return true;
    },
    canUndoApproval(shift) {
      // Only Admins can undo shift approvals
      if (this.userRole !== "Admin") return false;

      const status = this.getShiftStatus(shift);
      // Only allow undoing approval for approved shifts
      if (status !== "approved") return false;

      return true;
    },
    isCaregiverManagedByLead(caregiverId) {
      const managedCaregiver = this.availableCaregivers.find(
        (c) => c.id === Number(caregiverId) || c.id === String(caregiverId),
      );
      return !!managedCaregiver;
    },
    async handleClockIn(shift) {
      // Check if user is already clocked into another shift
      const activeShift = this.shifts.find(
        (s) =>
          s.id !== shift.id &&
          s.shift_status === "Clocked In" &&
          s.caregivers &&
          s.caregivers.some((c) => Number(c.id) === Number(this.userId)),
      );

      if (activeShift) {
        this.$notifyError(
          "You are already clocked into another shift. Please clock out of your current shift before clocking into a new one.",
        );
        return;
      }

      // Nudge toward the mobile app before opening the web clock-in flow.
      if (!(await this.$clockNudge("in"))) return;

      this.currentShift = shift;
      this.selectedGroupClients = [];
      this.currentLocation = null;

      // Reset VR distance flagging
      this.isVRDistanceFlagged = false;

      this.showClockInModal = true;
    },
    async handleClockOut(shift) {
      if (!(await this.$clockNudge("out"))) return;
      this.currentShift = shift;
      this.clockOutNotes = "";
      this.clockOutNotesError = "";
      this.showClockOutModal = true;
    },

    handleStartBreak(shift) {
      this.currentShift = shift;
      this.showStartBreakModal = true;
    },

    onBreakActionSuccess(response) {
      // Instantly update the isOnBreak flag based on the response
      if (this.currentShift && response && response.break) {
        // If breakEnd is null, the break was started; otherwise it was ended
        this.currentShift.isOnBreak = response.break.breakEnd === null;

        // Also update the shift in the shifts array
        const shiftIndex = this.shifts.findIndex(
          (s) => s.id === this.currentShift.id,
        );
        if (shiftIndex !== -1) {
          this.shifts[shiftIndex].isOnBreak = response.break.breakEnd === null;
        }
      }

      // Refresh shifts after break action
      this.loadShifts();
    },
    async requestLocation() {
      this.gettingLocation = true;

      try {
        // First, check the current permission status
        const permissionStatus = await geolocationService.checkPermission();

        if (permissionStatus.state === "denied") {
          this.$notifyError(
            "Location permission is denied. Please enable location access in your browser settings:\n\n" +
              "1. Click the location icon (🔒) in your browser address bar\n" +
              '2. Select "Allow" for location access\n' +
              "3. Refresh the page and try again",
          );
          return;
        }

        if (permissionStatus.state === "prompt") {
          this.$notifyInfo(
            "Please allow location access when prompted by your browser to continue.",
          );
        }

        // Attempt to get current position
        const position = await geolocationService.getCurrentPosition();

        this.currentLocation = {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        };

        const accuracyMessage =
          position.accuracy < 100
            ? "Location captured with good accuracy!"
            : position.accuracy < 500
              ? "Location captured (moderate accuracy - this is normal for laptops)"
              : "Location captured (low accuracy - this is normal for laptops)";

        this.$notifySuccess(accuracyMessage);
      } catch (error) {
        let errorMessage = "";
        if (error.message.includes("denied")) {
          errorMessage =
            "Location access was denied. Please enable location permissions in your browser settings and try again.";
        } else if (error.message.includes("unavailable")) {
          errorMessage =
            "Location services are unavailable. Please check your device settings and try again.";
        } else if (error.message.includes("timeout")) {
          errorMessage =
            "Location request timed out. Please try again or check your internet connection.";
        } else {
          errorMessage = `Location error: ${error.message}. Please try again or contact your administrator.`;
        }

        this.$notifyError(errorMessage);
      } finally {
        this.gettingLocation = false;
      }
    },

    async confirmClockOut() {
      // Validate notes before proceeding
      this.clockOutNotesError = "";

      if (!this.clockOutNotes || this.clockOutNotes.trim().length === 0) {
        this.clockOutNotesError = "Shift notes are required";
        this.$notifyError("Please enter shift notes before clocking out");
        return;
      }

      if (this.clockOutNotes.trim().length < 20) {
        this.clockOutNotesError = `Shift notes must be at least 20 characters (currently ${this.clockOutNotes.trim().length} characters)`;
        this.$notifyError("Shift notes must be at least 20 characters");
        return;
      }

      this.clockingOut = true;
      try {
        const isGroup = this.currentShift.shift_type === "Group";
        const now = new Date();

        const payload = {
          shiftId: this.currentShift.id,
          caregiverId: this.userId,
          isGroup: isGroup,
          notes: this.clockOutNotes.trim(),
          // Send UTC time
          utcTime: now.toISOString(),
          // Idempotency key — retried uploads replay instead of double-applying
          clientEventId: generateEventId(),
        };

        let response;
        try {
          response = await shiftsService.clockOut(payload);
        } catch (error) {
          // No connection: save the clock-out on this device, upload later.
          const isNetworkFailure =
            !navigator.onLine || (error.isAxiosError && !error.response);
          if (!isNetworkFailure) throw error;
          payload.offlineSync = true;
          enqueue("clock-out", payload, {
            shiftKey: payload.shiftId,
            label: "Clock-out",
          });
          this.$notifySuccess(
            "No internet connection — your clock-out was saved on this device and will upload automatically when you're back online.",
          );
          this.showClockOutModal = false;
          await this.loadShifts();
          return;
        }

        const hoursWorked = response.hoursWorked;
        this.$notifySuccess(
          `Successfully clocked out! Total hours: ${hoursWorked}`,
        );
        this.showClockOutModal = false;
        await this.loadShifts();
      } catch (error) {
        this.$notifyError(error.response?.data?.error || "Failed to clock out");
      } finally {
        this.clockingOut = false;
      }
    },
    formatClockTime(dateTime) {
      if (!dateTime) return "N/A";
      const date = new Date(dateTime);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    offlineRecordedTitle(row) {
      const parts = [];
      if (row.clock_in_offline) parts.push("clock-in");
      if (row.clock_out_offline) parts.push("clock-out");
      return `The ${parts.join(" and ")} for this shift ${parts.length > 1 ? "were" : "was"} recorded offline on the caregiver's device and uploaded later.`;
    },
    // Shadow/training side clock rows on a non-group shift (primaries on
    // 1-on-1 shifts never have a per-caregiver clock row).
    sideClockRows(shift) {
      if (!shift || (shift.shift_type || "1-on-1") === "Group") return [];
      return (shift.caregiverClockTimes || []).filter((ct) => ct.clock_in);
    },
    sideRowLabel(shift, ct) {
      const cg = (shift.caregivers || []).find(
        (c) => Number(c.id) === Number(ct.id_users),
      );
      const role = cg && cg.role && cg.role !== "primary"
        ? cg.role.charAt(0).toUpperCase() + cg.role.slice(1)
        : "Shadow";
      const name =
        (ct.caregiver && (ct.caregiver.name || `${ct.caregiver.first_name || ""} ${ct.caregiver.last_name || ""}`.trim())) ||
        (cg && (cg.name || `${cg.first_name || ""} ${cg.last_name || ""}`.trim())) ||
        "";
      const inTime = this.formatClockTime(ct.clock_in);
      if (!ct.clock_out) {
        return `${role} ${name}: ${inTime} - In Progress`;
      }
      const outTime = this.formatClockTime(ct.clock_out);
      const hours =
        Math.round(((new Date(ct.clock_out) - new Date(ct.clock_in)) / 36e5) * 100) / 100;
      return `${role} ${name}: ${inTime} - ${outTime} (${hours}h)`;
    },
    // The primary never clocked in but a shadow/training caregiver recorded
    // hours — without a marker the shift reads as if nobody showed up.
    hasOrphanedSideHours(shift) {
      if (shift.clock_in || shift.clockIn) return false;
      return this.sideClockRows(shift).length > 0;
    },
    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats
      const clockIn = shift.clock_in || shift.clockIn;
      const clockOut = shift.clock_out || shift.clockOut;

      const clockInTime = clockIn
        ? this.formatClockTime(clockIn)
        : "Not clocked in";
      const clockOutTime = clockOut
        ? this.formatClockTime(clockOut)
        : "Not clocked out";

      if (clockIn && clockOut) {
        return `${clockInTime} - ${clockOutTime}`;
      } else if (clockIn) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },
    // Format scheduled times that are stored as UTC times in TIME format
    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
        // If the date parsing failed, try to extract time portion
        const timeMatch = timeStr.match(/T(\d{2}):(\d{2})/);
        if (timeMatch) {
          const hour = parseInt(timeMatch[1]);
          const minute = timeMatch[2];
          const period = hour >= 12 ? "PM" : "AM";
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          return `${displayHour}:${minute} ${period}`;
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as UTC
      if (date) {
        const dateOnly = date.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },
    formatTime12Hour(timeStr) {
      if (!timeStr) return "";

      // Handle both HH:MM and HH:MM:SS formats
      const timeParts = timeStr.split(":");
      if (timeParts.length < 2) return timeStr; // Return as-is if not in expected format

      const hour = parseInt(timeParts[0]);
      const minute = parseInt(timeParts[1]);

      // Check for invalid numbers
      if (isNaN(hour) || isNaN(minute)) return timeStr;

      const period = hour >= 12 ? "pm" : "am";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },
    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      // Fallback: format as simple time strings
      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },
    async convertPotentialToActual(potentialShift) {
      try {
        const shiftData = {
          scheduled_start:
            potentialShift.scheduled_start || potentialShift.startTime,
          scheduled_end: potentialShift.scheduled_end || potentialShift.endTime,
          shift_type: potentialShift.shift_type,
          payer: potentialShift.payer,
          shift_notes: potentialShift.notes || "",
          shift_status: "Scheduled",
        };

        const newShift = await shiftsService.create(shiftData);

        const clientId =
          potentialShift.clients && potentialShift.clients.length > 0
            ? potentialShift.clients[0].id
            : null;
        const caregiverId =
          potentialShift.caregivers && potentialShift.caregivers.length > 0
            ? potentialShift.caregivers[0].id
            : null;

        await shiftsService.updateAssociations(newShift.id, {
          clientId: clientId,
          caregiverId: caregiverId,
        });

        await this.recordDeletedPotentialShift(potentialShift);

        return newShift;
      } catch (error) {
        throw error;
      }
    },
    async recordDeletedPotentialShift(potentialShift) {
      try {
        const deletedShiftData = {
          date: potentialShift.date.split("T")[0],
          scheduled_start: potentialShift.startTime,
          scheduled_end: potentialShift.endTime,
          client_id:
            potentialShift.clients && potentialShift.clients.length > 0
              ? potentialShift.clients[0].id
              : null,
          caregiver_id:
            potentialShift.caregivers && potentialShift.caregivers.length > 0
              ? potentialShift.caregivers[0].id
              : null,
          shift_type: potentialShift.shift_type,
          payer: potentialShift.payer,
          notes: `Converted to actual shift on login/edit`,
        };

        await deletedPotentialShiftsService.create(deletedShiftData);
      } catch (error) {}
    },
    handleApproveShift(shift) {
      if (!this.canApproveShift(shift)) {
        this.$notifyError("You don't have permission to approve this shift");
        return;
      }

      // Map the shift data for the approve modal
      const mappedShift = {
        ...shift,
        startTime: shift.scheduled_start || shift.startTime,
        endTime: shift.scheduled_end || shift.endTime,
        originalData: {
          ...shift,
          clockIn: shift.clock_in || shift.clockIn,
          clock_in: shift.clock_in || shift.clockIn,
          clockOut: shift.clock_out || shift.clockOut,
          clock_out: shift.clock_out || shift.clockOut,
          scheduled_start: shift.scheduled_start,
          scheduled_end: shift.scheduled_end,
        },
      };

      this.shiftToApprove = mappedShift;
      this.showApproveShiftModal = true;
    },
    async handleUndoApproval(shift) {
      if (!this.canUndoApproval(shift)) {
        this.$notifyError(
          "You don't have permission to undo this shift approval",
        );
        return;
      }

      try {
        // Confirm the action
        const confirmed = await this.$confirm(
          'Are you sure you want to undo this shift approval? This will restore hours back to the authorization and revert the shift to "Clocked Out" status.',
          "Confirm Undo Approval",
          {
            confirmButtonText: "Yes, Undo Approval",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );

        if (!confirmed) {
          return;
        }

        // Call the undo approval API
        await shiftsService.undoShiftApproval({ shiftId: shift.id });

        this.$notifySuccess(
          "Shift approval undone successfully! Authorization hours have been restored.",
        );

        // Reload shifts to reflect the change
        await this.loadShifts();
      } catch (error) {
        if (error !== "cancel") {
          this.$notifyError(
            error.response?.data?.error || "Failed to undo shift approval",
          );
        }
      }
    },
    canApproveMissedShift(shift) {
      // Only Admins can approve missed shifts
      if (this.userRole !== "Admin") return false;

      const status = this.getShiftStatus(shift);
      // Only allow approval for missed shifts
      if (status !== "missed") return false;

      const caregivers = shift.caregivers || [];
      if (caregivers.length === 0) return false;

      // Admins cannot approve a shift where they are an assigned caregiver
      if (caregivers.some(c => Number(c.id) === Number(this.userId))) {
        return false;
      }

      return true;
    },
    handleApproveMissedShift(shift) {
      if (!this.canApproveMissedShift(shift)) {
        this.$notifyError(
          "You don't have permission to approve this missed shift",
        );
        return;
      }

      // Map the shift data for the approve missed shift modal
      const mappedShift = {
        ...shift,
        scheduledStartTime: shift.scheduled_start || shift.startTime,
        scheduledEndTime: shift.scheduled_end || shift.endTime,
        startTime: shift.scheduled_start || shift.startTime,
        endTime: shift.scheduled_end || shift.endTime,
        service_codes: shift.service_codes,
        non_service_codes: shift.non_service_codes,
        originalData: {
          ...shift,
          scheduled_start: shift.scheduled_start,
          scheduled_end: shift.scheduled_end,
          service_codes: shift.service_codes,
          non_service_codes: shift.non_service_codes,
        },
      };

      this.shiftToApproveMissed = mappedShift;
      this.showApproveMissedShiftModal = true;
    },
    canRecheckAuth(shift) {
      // Only Admins/Leads can re-check authorizations
      if (this.userRole !== "Admin" && this.userRole !== "Lead") return false;
      // Only for shifts flagged by the nightly auth look-ahead.
      return (
        this.getShiftStatus(shift) === "error" && shift.auth_hours_error === true
      );
    },
    async handleRecheckAuth(shift) {
      if (!this.canRecheckAuth(shift)) return;
      try {
        const result = await shiftsService.recheckAuth(shift.id);
        if (result && result.cleared) {
          this.$notifySuccess(
            result.message || "Authorization available — shift rescheduled.",
          );
          await this.loadShifts();
        } else {
          this.$notifyInfo
            ? this.$notifyInfo(
                result?.message ||
                  "Still not enough authorized hours/units remaining for this shift.",
              )
            : this.$notifyError(
                result?.message ||
                  "Still not enough authorized hours/units remaining for this shift.",
              );
        }
      } catch (error) {
        this.$notifyError(
          error.response?.data?.error || "Error re-checking authorization",
        );
      }
    },
    canChangeServiceCode(shift) {
      // Only Admins can change service codes
      if (this.userRole !== "Admin") return false;

      const status = this.getShiftStatus(shift);
      // Only allow service code changes for approved shifts
      if (status !== "approved") return false;

      // Must have service codes and billing hours
      if (!shift.service_codes || shift.service_codes.length === 0)
        return false;
      if (!shift.billing_hours || parseFloat(shift.billing_hours) <= 0)
        return false;

      return true;
    },
    openChangeServiceCodeModal(shift) {
      if (!this.canChangeServiceCode(shift)) {
        this.$notifyError(
          "You don't have permission to change the service code for this shift",
        );
        return;
      }

      // Map the shift data for the modal
      const mappedShift = {
        id: shift.id,
        date: shift.date || shift.scheduled_start,
        startTime: shift.scheduled_start || shift.startTime,
        endTime: shift.scheduled_end || shift.endTime,
        scheduled_start: shift.scheduled_start,
        scheduled_end: shift.scheduled_end,
        billing_hours: shift.billing_hours,
        service_codes: shift.service_codes,
        serviceCodes: shift.service_codes,
        clients: shift.clients,
        client:
          shift.clients && shift.clients.length > 0
            ? shift.clients[0].name
            : "Unassigned",
        caregivers: shift.caregivers,
        caregiver:
          shift.caregivers && shift.caregivers.length > 0
            ? shift.caregivers[0].name
            : "Unassigned",
      };

      this.shiftToChangeServiceCode = mappedShift;
      this.showChangeServiceCodeModal = true;
    },
    async viewShiftHistory(shift) {
      this.showShiftHistoryModal = true;
      this.loadingHistory = true;
      this.shiftHistory = [];

      // Capture the instance date from the grid row (for recurring shifts)
      // Extract just the date part (YYYY-MM-DD) for the API call
      let occurrenceDate = null;
      if (shift.date) {
        occurrenceDate = shift.date.split('T')[0];
      } else if (shift.scheduled_start) {
        // Extract date in Mountain Time from the scheduled_start
        const startDate = new Date(shift.scheduled_start);
        occurrenceDate = startDate.toLocaleDateString('en-CA', { timeZone: 'America/Denver' });
      }
      this.shiftInstanceDate = occurrenceDate;

      try {
        // Fetch the shift history with occurrence date for correct status computation
        const historyResponse = await shiftsService.getShiftHistory(shift.id, occurrenceDate);
        this.shiftHistory = historyResponse.history || [];

        // Use the shift details from the history response (includes computed occurrence status)
        // Fall back to original shift data if not available
        if (historyResponse.shift) {
          this.shiftForHistory = historyResponse.shift;
        } else {
          // Fallback: use the original shift data from the grid (which has the correct status)
          this.shiftForHistory = shift;
        }
      } catch (error) {
        this.$notifyError("Failed to load shift history");
        // Fallback to original shift data from the grid
        this.shiftForHistory = shift;
      } finally {
        this.loadingHistory = false;
      }
    },
    canEditShift(shift) {
      const status = (shift.status || shift.shift_status)?.toLowerCase();
      const isClocked =
        status === "clocked-in" ||
        status === "clocked in" ||
        status === "clocked_in";
      const isClockedOut =
        status === "clocked-out" ||
        status === "clocked out" ||
        status === "clocked_out";
      const isApproved = status === "approved";
      const isMissed = status === "missed";
      const isError = status === "error";

      // Approved shifts: Admins may edit documentation only (notes, question
      // answers, activities) via EditShiftNotesModal — see editShift. Times
      // and payer/service code still require Undo Approval / Change Service Code.
      if (isApproved) {
        return this.userRole === "Admin" && shift.shift_type !== "Group";
      }
      if (status === "canceled") {
        return false;
      }

      // For missed shifts, allow Admins, Leads, Caregivers, and Employment Specialists to edit
      if (isMissed) {
        // Admins and Leads can always edit missed shifts
        if (this.userRole === "Admin" || this.userRole === "Lead") {
          return true;
        }

        // Caregivers can edit their own missed shifts (for notes and activities)
        if (this.userRole === "Caregiver") {
          if (!shift.caregivers || shift.caregivers.length === 0) {
            return false;
          }
          const caregiverId = shift.caregivers[0].id;
          return caregiverId === Number(this.userId);
        }

        // Employment Specialists can edit their own missed shifts (for notes and activities)
        if (this.userRole === "Employment Specialist") {
          if (!shift.caregivers || shift.caregivers.length === 0) {
            return false;
          }
          const caregiverId = shift.caregivers[0].id;
          return caregiverId === Number(this.userId);
        }

        return false;
      }

      // For clocked-in shifts, admins, leads, and employment specialists can edit OTHER people's shifts
      if (isClocked) {
        // Admins can always edit clocked-in group shifts (to manage caregiver assignments)
        if (shift.shift_type === "Group" && this.userRole === "Admin") {
          return true;
        }

        const hasPermission =
          this.userRole === "Admin" ||
          this.userRole === "Lead" ||
          this.userRole === "Employment Specialist";
        if (!hasPermission) return false;

        // Admins and Leads cannot edit their own clocked-in shifts at all
        if (this.userRole === "Admin" || this.userRole === "Lead") {
          if (shift.caregivers && shift.caregivers.length > 0) {
            const isAssigned = shift.caregivers.some(
              (c) => Number(c.id) === Number(this.userId)
            );
            if (isAssigned) {
              return false; // Don't show edit button for their own clocked-in shifts
            }
          }
        }

        return true;
      }

      // For error status shifts (auto clocked out), allow same permissions as clocked-out shifts
      // (opens Edit Clock Out Time modal for others, Edit Notes modal for their own shifts)
      if (isError) {
        return (
          this.userRole === "Admin" ||
          this.userRole === "Lead" ||
          this.userRole === "Employment Specialist"
        );
      }

      // For clocked-out shifts, allow caregivers/leads/employment specialists to edit notes and activities
      if (isClockedOut) {
        // Admins can edit
        if (this.userRole === "Admin") {
          return true;
        }

        // Leads can edit all shifts (clock times for others, notes/activities for themselves)
        if (this.userRole === "Lead") {
          return true;
        }

        // Caregivers can edit their own clocked-out shifts (for notes and activities)
        if (this.userRole === "Caregiver") {
          if (!shift.caregivers || shift.caregivers.length === 0) {
            return false;
          }
          const caregiverId = shift.caregivers[0].id;
          return caregiverId === Number(this.userId);
        }

        // Employment Specialists can edit their own clocked-out shifts (for notes and activities)
        if (this.userRole === "Employment Specialist") {
          if (!shift.caregivers || shift.caregivers.length === 0) {
            return false;
          }
          const caregiverId = shift.caregivers[0].id;
          return caregiverId === Number(this.userId);
        }

        return false;
      }

      // For non-clocked shifts, use existing logic
      // Admins can edit all shifts
      if (this.userRole === "Admin") {
        return true;
      }

      // Leads can edit all scheduled shifts
      if (this.userRole === "Lead") {
        return true;
      }

      // Caregivers cannot edit any shifts (except clocked-out, handled above)
      if (this.userRole === "Caregiver") {
        return false;
      }

      // Employment Specialists can only edit their own shifts
      if (this.userRole === "Employment Specialist") {
        if (!shift.caregivers || shift.caregivers.length === 0) {
          return false;
        }
        const caregiverId = shift.caregivers[0].id;
        return caregiverId === Number(this.userId);
      }

      return false;
    },
    isShiftClockedIn(shift) {
      const status = (shift.status || shift.shift_status || "").toLowerCase();
      return (
        status === "clocked-in" ||
        status === "clocked in" ||
        status === "clocked_in"
      );
    },
    canDeleteShift(shift) {
      const status = (shift.status || shift.shift_status)?.toLowerCase();

      // For clocked in/out shifts, only Admins can delete
      if (
        status === "clocked-in" ||
        status === "clocked in" ||
        status === "clocked_in" ||
        status === "clocked-out" ||
        status === "clocked out" ||
        status === "clocked_out"
      ) {
        return this.userRole === "Admin";
      }

      // Cannot delete approved or error shifts
      if (status === "approved" || status === "error") {
        return false;
      }

      // For canceled shifts, only Admins can delete
      if (status === "canceled") {
        return this.userRole === "Admin";
      }

      // For missed shifts, only Admins and Leads can delete
      if (status === "missed") {
        return this.userRole === "Admin" || this.userRole === "Lead";
      }

      // For other statuses, use same permissions as edit
      return this.canEditShift(shift);
    },
    canCancelShift(shift) {
      if (this.userRole !== "Admin") return false;
      const status = (shift.status || shift.shift_status)?.toLowerCase();
      return ["unassigned", "scheduled", "missed"].includes(status);
    },
    async cancelShift(shift) {
      const original = shift.originalData || shift;
      const shiftId = shift.id || original.id;
      if (!shiftId) return;

      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      // For a single (non-materialized) occurrence of a recurring series the id
      // we hold is the recurring TEMPLATE id, so we must tell the API which date
      // and scope to act on — otherwise the whole series gets canceled.
      let cancelTargetId = shiftId;
      let cancelExtra = {};
      if (isRecurring) {
        let scope;
        try {
          await this.$confirm(
            "This is a recurring shift. Cancel only this occurrence, or the entire series?",
            "Cancel Recurring Shift",
            {
              confirmButtonText: "Only This Occurrence",
              cancelButtonText: "Entire Series",
              distinguishCancelAndClose: true,
              type: "warning",
            },
          );
          scope = "occurrence";
        } catch (action) {
          if (action === "cancel") {
            scope = "series";
          } else {
            return; // dialog dismissed
          }
        }

        if (scope === "occurrence") {
          const shiftDate = shift.date
            ? shift.date.split("T")[0]
            : original.date
              ? original.date.split("T")[0]
              : null;
          if (!shiftDate) {
            this.$notifyError(
              "Could not determine the occurrence date to cancel.",
            );
            return;
          }
          cancelExtra = { date: shiftDate };
        } else {
          // Cancel the whole series — target the recurring template.
          cancelTargetId =
            original.parent_recurring_shift_id ||
            shift.parent_recurring_shift_id ||
            original.original_recurring_shift_id ||
            shiftId;
          cancelExtra = { cancelEntireSeries: true };
        }
      }

      try {
        await this.$prompt(
          "Please provide a reason for canceling this shift:",
          "Cancel Shift",
          {
            confirmButtonText: "Cancel Shift",
            cancelButtonText: "Go Back",
            inputType: "textarea",
            inputPlaceholder: "e.g., client canceled, caregiver sick, weather...",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "A cancellation reason is required";
              return true;
            },
            beforeClose: async (action, instance, done) => {
              if (action !== "confirm") {
                done();
                return;
              }
              const reason = instance.inputValue;
              if (!reason || !reason.trim()) { done(); return; }

              instance.confirmButtonLoading = true;
              instance.confirmButtonText = "Canceling...";
              try {
                await shiftsService.update(cancelTargetId, {
                  shift_status: "Canceled",
                  adminChangeReason: reason.trim(),
                  ...cancelExtra,
                });
                this.$notifySuccess("Shift canceled successfully");
              } catch (error) {
                this.$notifyError(
                  error.response?.data?.message || "Error canceling shift",
                );
              } finally {
                instance.confirmButtonLoading = false;
                instance.confirmButtonText = "Cancel Shift";
                done();
                this.loadShifts();
              }
            },
          },
        );
      } catch {
        // User clicked "Go Back"
      }
    },
    getShiftNotes(shift) {
      // For canceled shifts, show the cancellation reason
      if (shift.cancel_reason) {
        return `Canceled: ${shift.cancel_reason}`;
      }

      // Return the appropriate notes field based on shift type/payer

      // Check if this is a Job Development service code
      const isJDService =
        shift.service_codes &&
        shift.service_codes.some(
          (sc) =>
            sc.code === "CE" ||
            sc.code === "SE" ||
            sc.code === "SJBT" ||
            sc.code === "TWE",
        );

      // For JD services, ONLY show activity, outcome, and next steps (not shift_notes)
      if (isJDService) {
        // Only show JD fields for JD services, even if empty
        if (shift.jd_activity || shift.jd_outcome || shift.jd_next_steps) {
          const jdNotes = [];
          if (shift.jd_activity) jdNotes.push(`Activity: ${shift.jd_activity}`);
          if (shift.jd_outcome) jdNotes.push(`Outcome: ${shift.jd_outcome}`);
          if (shift.jd_next_steps)
            jdNotes.push(`Next Steps: ${shift.jd_next_steps}`);
          return jdNotes.join("\n");
        }
        // Return empty for JD services without JD fields (don't fall back to shift_notes)
        return "";
      }

      // For non-JD services, show regular shift notes
      // Use shift_notes first (all new data uses this field)
      // Fall back to legacy service-specific fields for backward compatibility
      const shiftNotes = shift.shift_notes || shift.notes || shift.jc_shift_notes || "";

      // For group shifts, prepend group_name to notes if it exists
      if (shift.shift_type === 'Group' && shift.group_name) {
        return shiftNotes ? `${shift.group_name} - ${shiftNotes}` : shift.group_name;
      }

      return shiftNotes;
    },
    calculateShiftMinutes(startTime, endTime) {
      if (!startTime || !endTime) return 0;

      const extractTime = (timeStr) => {
        if (!timeStr) return null;
        if (timeStr.includes('T')) {
          const date = new Date(timeStr);
          return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        }
        return timeStr;
      };

      const startTimeStr = extractTime(startTime);
      const endTimeStr = extractTime(endTime);

      if (!startTimeStr || !endTimeStr) return 0;

      const [startHour, startMin] = startTimeStr.split(':').map(Number);
      const [endHour, endMin] = endTimeStr.split(':').map(Number);

      if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
        return 0;
      }

      const startMinutes = startHour * 60 + startMin;
      let endMinutes = endHour * 60 + endMin;

      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }

      return endMinutes - startMinutes;
    },
    minutesToHoursFormat(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}:${String(mins).padStart(2, '0')}`;
    },
    getTimeTooltip(row) {
      const scheduledStart = row.scheduled_start || row.startTime;
      const scheduledEnd = row.scheduled_end || row.endTime;
      const scheduledMinutes = this.calculateShiftMinutes(scheduledStart, scheduledEnd);
      let tooltip = `Hours Scheduled: ${this.minutesToHoursFormat(scheduledMinutes)}`;

      const status = this.getShiftStatus(row);
      if ((status === 'clocked-out' || status === 'approved') && (row.clock_in || row.clockIn) && (row.clock_out || row.clockOut)) {
        const clockIn = row.clock_in || row.clockIn;
        const clockOut = row.clock_out || row.clockOut;
        const workedMinutes = this.calculateShiftMinutes(clockIn, clockOut);
        tooltip += `\nHours Worked: ${this.minutesToHoursFormat(workedMinutes)}`;
      }

      return tooltip;
    },
    isVRProximityFlagged(shift) {
      // Check if this is a VR shift with proximity flag in notes
      if (shift.payer !== "VR") {
        return false;
      }

      const notes = this.getShiftNotes(shift);
      return (
        notes.includes("VR SHIFT - PROXIMITY") ||
        notes.includes("⚠️ VR SHIFT - PROXIMITY")
      );
    },
  },
};
</script>

<style scoped>
/* Multiple Indicator Styling */
.multiple-indicator {
  cursor: default;
}

/* Clock Modal Styling - Match Edit Modal Width */
.clock-modal-content {
  max-width: 600px;
  min-width: 500px;
  margin: 0 auto;
  padding: 0 2rem;
}

.clock-modal-content .mb-3 {
  margin-bottom: 1.5rem;
}

/* Constrain modal fields to modal width */
.clock-modal-content .row {
  margin-left: 0;
  margin-right: 0;
  max-width: 100%;
  width: 100%;
}

.clock-modal-content .row > [class*="col-"] {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  flex: 1 1 0%;
  max-width: 100%;
}

/* Ensure input fields don't expand beyond container */
.clock-modal-content .el-input,
.clock-modal-content .form-control {
  max-width: 100%;
  box-sizing: border-box;
}

.clock-modal-content h5 {
  margin-bottom: 1rem;
  color: #fff;
}

.clock-modal-content p {
  margin-bottom: 0.5rem;
  color: #fff;
}

.clock-modal-content .form-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

/* Time Display Styling */
.time-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.actual-time-primary {
  font-weight: normal;
}

.scheduled-time-primary {
  font-weight: normal;
}

.scheduled-time-secondary {
  font-size: 0.85em;
  color: #6ba6cd;
}

.scheduled-time-secondary small {
  color: #6ba6cd !important;
}

.clock-location-indicator {
  margin-top: 0.15rem;
  font-size: 0.8em;
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.clock-loc-link {
  color: #6ba6cd;
  white-space: nowrap;
}

.clock-loc-link:hover {
  text-decoration: underline;
}

.clock-loc-note {
  color: #909399;
  white-space: nowrap;
}

/* Compact Filter Section */
.compact-filter-section {
  --primary-color: #6ba6cd;
  --primary-hover: #5a94b8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0;
}

/* Filter Rows */
.filter-row-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  transition: all 0.2s ease;
  flex-wrap: wrap;
}

.filter-row-compact:hover {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.filter-row-compact.navigation-row {
  justify-content: center;
}

.filter-row-compact.data-row {
  justify-content: center;
}

.filter-group-compact {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group-compact.date-group {
  min-width: 320px;
  flex-grow: 1;
  max-width: 480px;
}

/* Prev/next arrows live inside the date group, flanking the picker */
.filter-group-compact.date-nav-group {
  gap: 0.5rem;
}

.date-nav-group .compact-date-picker {
  flex: 1;
  min-width: 0;
}

/* Compact Select Styling */
.compact-select {
  min-width: 120px;
}

:deep(.compact-select .el-input__inner) {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 6px !important;
  color: var(--text-primary) !important;
  height: 40px !important;
  font-size: 13px !important;
  transition: all 0.2s ease !important;
  padding: 0 12px !important;
}

:deep(.compact-select .el-input__inner:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

:deep(.compact-select .el-input__inner:focus) {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 2px rgba(205, 144, 189, 0.1) !important;
}

:deep(.compact-select .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
  font-size: 12px !important;
}

/* Compact Date Picker */
.compact-date-picker {
  width: 100%;
}

::v-deep .compact-date-picker .el-input__inner {
  text-overflow: clip;
  white-space: nowrap;
}

:deep(.modern-date-picker-popper) {
  z-index: 9999 !important;
  background: rgba(45, 45, 45, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
}

/* Date Range Picker Styling */
::v-deep .el-date-editor {
  width: 100%;
}

::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

::v-deep .el-range__close-icon {
  right: 10px !important;
}

::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}

/* Compact Navigation Button */
.compact-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  width: 40px;
  min-width: 40px;
}

.compact-nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.compact-nav-btn:active {
  transform: translateY(0);
}

.compact-nav-btn i {
  font-size: 12px;
}

/* Compact Today Button */
.compact-today-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--primary-hover)
  );
  border: none;
  border-radius: 6px;
  color: white;
  padding: 0 0.75rem;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(205, 144, 189, 0.2);
  height: 40px;
  white-space: nowrap;
  min-width: 70px;
}

.compact-today-btn:hover {
  background: linear-gradient(135deg, var(--primary-hover), #a67491);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(205, 144, 189, 0.3);
}

.compact-today-btn:active {
  transform: translateY(0);
}

.compact-today-btn i {
  font-size: 0.75rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filter-row-compact {
    gap: 0.75rem;
  }

  .compact-select {
    min-width: 110px;
  }

  .filter-group-compact.date-group {
    min-width: 240px;
  }
}

@media (max-width: 768px) {
  .compact-filter-section {
    padding: 0 0.5rem;
    gap: 0.5rem;
  }

  .filter-row-compact {
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
  }

  /* Navigation: row 1 = [period select][Today], row 2 = [<][date][>] */
  .filter-row-compact.navigation-row {
    flex-wrap: wrap;
  }

  .navigation-row .filter-group-compact.view-group {
    order: 1;
    flex: 1 1 0%;
    min-width: 0;
  }

  .navigation-row .filter-group-compact.today-group {
    order: 2;
    flex: 0 0 auto;
  }

  .navigation-row .filter-group-compact.date-group {
    order: 3;
    flex: 1 1 100%;
    min-width: 0;
    max-width: unset;
  }

  .view-group .compact-select {
    width: 100%;
    min-width: 0;
  }

  /* Data filters: two-column grid instead of a tall single stack */
  .filter-row-compact.data-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .data-row .filter-group-compact {
    min-width: 0;
    width: 100%;
  }

  .data-row .compact-select {
    width: 100%;
    min-width: 0;
  }
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status badge colors matching calendar page */
.badge-secondary {
  /* Unassigned - Teal */
  background: linear-gradient(
    135deg,
    rgba(32, 201, 151, 0.7),
    rgba(32, 201, 151, 0.5)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-info {
  /* Scheduled - Bright blue */
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-success {
  /* Clocked In - Bright yellow */
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  );
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.badge-warning {
  /* Clocked Out - Muted orange */
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-primary {
  /* Approved - Muted deep purple */
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-danger {
  /* Error - Dark magenta */
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.8),
    rgba(220, 53, 69, 0.6)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-missed {
  /* Missed - Orange */
  background: linear-gradient(
    135deg,
    rgba(108, 117, 125, 0.8),
    rgba(108, 117, 125, 0.6)
  );
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.badge-canceled {
  /* Canceled - Dark brown-black */
  background: linear-gradient(
    135deg,
    rgba(75, 50, 30, 0.9),
    rgba(75, 50, 30, 0.7)
  );
  color: #fff;
  border: 1px solid rgba(75, 50, 30, 0.3);
}

.d-flex {
  display: flex;
}

.btn-sm {
  transition: all 0.2s ease;
}

.btn-sm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.clock-modal-content {
  padding: 0;
}

.clock-modal-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.clock-modal-content h6 {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 1rem;
}

.clock-modal-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.clock-modal-content strong {
  color: #fff;
  font-weight: 600;
}

/* Form styling to match the app's dark theme */
.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Success text styling */
.text-success {
  color: #28a745 !important;
  font-weight: 500;
}

/* Simple, clean styling for modal elements */
.location-details {
  margin-left: 0.5rem;
}

.location-address {
  color: #fff;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.map-link-btn {
  color: #6ba6cd !important;
  padding: 0 !important;
  font-size: 0.85rem;
}

.map-link-btn:hover {
  color: #8bc4e0 !important;
}

.text-muted {
  color: #b0b0b0 !important;
}

/* Ensure all action buttons have consistent height */
.btn-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  height: 32px; /* Set consistent height for all small buttons */
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-sm i {
  margin-right: 0.25rem;
}

/* Character count styling */
.character-count {
  font-size: 0.85rem;
  margin-top: 0.25rem;
  font-weight: 500;
  transition: color 0.2s ease;
}

.character-count.text-danger {
  color: #dc3545 !important;
}

.character-count.text-success {
  color: #28a745 !important;
}

/* Error message styling */
.error-message {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

/* Invalid input styling */
:deep(.el-textarea.is-invalid .el-textarea__inner) {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1) !important;
}

:deep(.el-textarea.is-invalid .el-textarea__inner:focus) {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15) !important;
}

/* Location selector styling */
.location-selector {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.25rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
}

.location-option {
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.02);
  transition: all 0.2s ease;
}

.location-option:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.location-radio {
  width: 100%;
}

:deep(.location-radio .el-radio__label) {
  width: 100%;
  padding-left: 0.5rem;
}

.location-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.location-address {
  color: #e0e0e0;
  font-size: 0.9rem;
  flex-grow: 1;
  margin-right: 1rem;
}

.map-link-btn {
  color: #6ba6cd !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.8rem !important;
  min-height: auto !important;
  height: auto !important;
}

.map-link-btn:hover {
  color: #5a94b8 !important;
}

.map-link-btn i {
  margin-right: 0.25rem;
}

/* Geofence info styling */
.geofence-info {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.1),
    rgba(255, 255, 255, 0.95)
  );
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.geofence-info h6 {
  color: #f39c12;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.geofence-info p {
  margin-bottom: 0;
  color: #555;
  font-size: 0.85rem;
  line-height: 1.4;
}

.geofence-info strong {
  color: #e67e22;
  font-weight: 600;
}

/* VR Location Form Styling */
.vr-location-form {
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.1),
    rgba(255, 255, 255, 0.95)
  );
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.vr-location-form .row {
  margin: 0;
}

.vr-location-form .col-md-6,
.vr-location-form .col-md-3 {
  padding: 0 0.25rem;
}

.vr-location-form .mb-2 {
  margin-bottom: 0.5rem !important;
}

:deep(.vr-location-form .el-input) {
  width: 100%;
}

:deep(.vr-location-form .el-input__inner) {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(40, 167, 69, 0.2) !important;
  border-radius: 4px !important;
  color: #333 !important;
  font-size: 0.9rem !important;
}

:deep(.vr-location-form .el-input__inner:focus) {
  border-color: rgba(40, 167, 69, 0.6) !important;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.1) !important;
}

:deep(.vr-location-form .el-input__inner::placeholder) {
  color: rgba(0, 0, 0, 0.5) !important;
  font-size: 0.85rem !important;
}

/* Shift Status Container and Proximity Flag */
.shift-status-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.offline-flag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.4rem;
  border-radius: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  cursor: help;
}

.pending-sync-flag {
  background: rgba(230, 162, 60, 0.9);
  color: #fff;
}

.offline-recorded-flag {
  background: rgba(108, 117, 125, 0.85);
  color: #fff;
}

.shadow-hours-flag {
  background: rgba(64, 158, 255, 0.85);
  color: #fff;
}

.side-clock-line {
  font-size: 0.85em;
}

.side-clock-line small {
  color: #6ba6cd !important;
  font-style: italic;
}

.proximity-flag {
  display: inline-block;
  background: linear-gradient(
    135deg,
    rgba(255, 152, 0, 0.9),
    rgba(255, 193, 7, 0.8)
  );
  color: #212529;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.4rem;
  border-radius: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  animation: proximityPulse 2s ease-in-out infinite alternate;
  cursor: help;
}

@keyframes proximityPulse {
  0% {
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.2),
      0 0 0 0 rgba(255, 152, 0, 0.4);
  }
  100% {
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.2),
      0 0 0 4px rgba(255, 152, 0, 0);
  }
}

/* Service code column styling */
:deep(.aiv-table th:nth-child(5)),
:deep(.aiv-table td:nth-child(5)) {
  text-align: center;
}

/* Notes column and cell styling */
:deep(.aiv-table th:nth-child(7)),
:deep(.aiv-table td:nth-child(7)) {
  width: 300px;
  max-width: 300px;
}

.notes-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mobile responsiveness for clock-in/clock-out modals */
@media (max-width: 768px) {
  .mobile-friendly-modal .el-dialog {
    width: 95% !important;
    margin: 5vh auto !important;
  }

  .clock-modal-content {
    min-width: auto !important;
  }

  .mobile-friendly-modal .el-dialog__body {
    padding: 10px 15px !important;
  }

  .clock-modal-content {
    padding: 0 5px;
  }

  .clock-modal-content .el-button {
    width: 100%;
    margin-bottom: 10px;
  }

  .clock-modal-content .location-selector {
    margin-bottom: 15px;
  }

  .clock-modal-content .location-option {
    margin-bottom: 10px;
  }

  .clock-modal-content .el-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .notes-cell {
    max-width: 200px;
  }
}

/* Clock In button - matches "Clocked In" status color (bright yellow) */
.clock-in-btn {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  color: #000 !important;
}
.clock-in-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 1),
    rgba(255, 193, 7, 0.8)
  ) !important;
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
  color: #000 !important;
}

/* Clock Out button - matches "Clocked Out" status color (muted orange) */
.clock-out-btn {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.clock-out-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.8),
    rgba(237, 137, 54, 0.6)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Start Break button - Teal/Cyan color to distinguish from Clock Out */
.start-break-btn {
  background: linear-gradient(
    135deg,
    rgba(23, 162, 184, 0.8),
    rgba(23, 162, 184, 0.6)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}
.start-break-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(23, 162, 184, 0.9),
    rgba(23, 162, 184, 0.7)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

</style>
